import {
  FISH_SPECIES_LIST,
  fishHealthMilli,
  JEWEL_MOVE_SPEED_MAX_BPS,
  type OpenWorldBossEarlySpell,
  type OpenWorldBossFinalPressure,
  type OpenWorldBossLateSpell,
  type OpenWorldBossMeleeBlow,
  type OpenWorldBossStrikeAbility,
  RARITIES,
  type Rarity,
} from '@kenocyber/shared';
import { MILLI_TICKS_PER_TICK } from '../../hull/attack-speed';
import { SHIP_BASE_MAX_HEALTH_MILLI } from '../../hull/base-health';
import { HUNT_BALANCE } from '../../hunt/balance';
import {
  PVP_BOMB_BLAST_RADIUS_MU,
  PVP_BOMB_BODY_RADIUS_MU,
  PVP_BOMB_KNOCKBACK_MU,
  PVP_BOMB_PROXIMITY_RADIUS_MU,
  PVP_BOMB_SPEED_BPS,
  PVP_CARD_HOMING_BPS,
  PVP_CARD_PROJECTILE_LIFETIME_TICKS,
  PVP_CARD_PROJECTILE_SPEED_BPS,
  PVP_CARD_UNLIMITED_REACH_BPS,
  PVP_CATOLE_BLAST_RADIUS_MU,
  PVP_CATOLE_KNOCKBACK_MU,
  PVP_FIRE_INTERVAL_TICKS,
  PVP_FIREBALL_CHARGE_MS,
  PVP_FIREBALL_REACH_BPS,
  PVP_FIREBALL_SPEED_BPS,
  PVP_FREEZE_ATTACK_BLOCK_BPS,
  PVP_FREEZE_DURATION_STEPS,
  PVP_ICE_BOLT_SPEED_BPS,
  PVP_MISSILE_BODY_RADIUS_MU,
  PVP_MISSILE_HIT_RADIUS_MU,
  PVP_MISSILE_LIFETIME_TICKS,
  PVP_MISSILE_SPEED_MU_PER_TICK,
  PVP_POISON_TICK_INTERVAL_TICKS,
  PVP_PROJECTILE_DAMAGE,
  PVP_PROJECTILE_LIFETIME_TICKS,
  PVP_PROJECTILE_RADIUS_MU,
  PVP_PROJECTILE_SPEED_MU_PER_TICK,
  PVP_PROJECTILE_SUBSTEPS,
  PVP_SHIP_RADIUS_MU,
  PVP_SHIP_SPEED_MU_PER_TICK,
  PVP_SLOW_DURATION_STEPS,
  PVP_SOLAR_LANCE_REACH_BPS,
  PVP_SOLAR_LANCE_WIDTH_BPS,
  PVP_SUMMON_MUZZLE_MU,
  PVP_TICK_MS,
} from '../../pvp/balance';
import { OPEN_WORLD_QUADRANT_SIDE_MU } from '../geometry';
import { OPEN_WORLD_VISIBLE_GUARANTEED_MU, OPEN_WORLD_VISIBLE_REACH_MU } from '../viewport';

/**
 * **Every tunable number an open-world quadrant's room has. Provisional.**
 *
 * ## Read from the duel, never copied out of it
 *
 * The gate's instruction was "exactly the duel's semantics, and do not copy the
 * formulas or the constants". So the ship's travel per tick, the tick itself,
 * the hull radius, the shot's speed, radius, lifetime, substeps, damage and
 * cadence are IMPORTED from `pvp/balance.ts` and re-exported under this
 * module's names. There is one fleet and one physics; a literal written here
 * would be a second one that agrees only until somebody edits either.
 *
 * `open-world/geometry.ts` already does exactly this - the whole quadrant's
 * side is derived from `pvpTickTravelMu()` - so this file continues an
 * established coupling rather than inventing one.
 *
 * What the coupling means, said plainly: if the duel's base ship gets faster,
 * the world gets bigger, its interest area gets wider and its shots fly further
 * per tick, all at once. That is the correct direction. The requirement was
 * expressed in minutes and screens, and minutes and screens are speed and
 * distance.
 *
 * ## What is NOT decided here
 *
 * - **How many players a quadrant holds.** Deliberately absent. The gate says
 *   the ceiling is not fixed yet and that admission belongs to a future global
 *   coordinator; a number written here would be that decision taken by
 *   accident.
 * - **The shots-per-second ceiling.** Deliberately absent. The gate defers it
 *   to a calibration at the end. Nothing in this room caps the cadence a jewel
 *   grants, which is also `CLAUDE.md`'s standing rule.
 * - **Anything that pays.** No reward, no gold, no drop, no experience, no
 *   respawn. A quadrant's room decides who is where and who is hurt, and
 *   nothing at all about who is owed.
 *
 * Everything below that is not imported is marked provisional and stays that
 * way until the final benchmark measures it.
 */

/* -------------------------------------------------------------------------- */
/*  Time                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The authoritative tick: **50 ms**, the same one a duel runs at.
 *
 * Imported rather than written, so a quadrant can never tick at a different
 * rate from the movement its own size was derived from.
 */
export const OPEN_WORLD_TICK_MS = PVP_TICK_MS;

/**
 * How much observed time one invocation may take in, in milliseconds.
 *
 * ## What it is really for: not consuming an outage
 *
 * The room's clock is not `Date.now()`. It is the sum of the intervals the room
 * ACTUALLY OBSERVED while it was running, each one clamped by this. A room that
 * is pumped every second - which the watchdog guarantees while anybody is
 * disconnected - accrues the true elapsed time, because a second is far below
 * this bound. A room that was not running at all for ten minutes, because the
 * runtime was down or a deployment was rolling, accrues **five seconds** for
 * the whole gap.
 *
 * That is how the three-minute reconnection window excludes downtime: the
 * window is measured against this clock, so time in which nobody could have
 * reconnected - because there was nothing to reconnect to - is not spent.
 *
 * Five seconds rather than the watchdog's one, so ordinary scheduling jitter,
 * a slow alarm or a garbage collection pause is absorbed and only a real gap is
 * cut short.
 */
export const OPEN_WORLD_MAX_OBSERVED_MS = 5_000;

/**
 * The most ticks one invocation may simulate before returning.
 *
 * The accumulator KEEPS the remainder, so this is not lost time - it is a bound
 * on how long a single message or alarm may hold the runtime. Two hundred ticks
 * is ten seconds of world, which is more than {@link OPEN_WORLD_MAX_OBSERVED_MS}
 * can ever hand it, so on a healthy room this is never reached; it exists so a
 * clock that jumps cannot turn one invocation into an unbounded loop.
 */
export const OPEN_WORLD_MAX_TICKS_PER_STEP = 200;

/**
 * How often the room wakes itself when nothing is arriving. One second.
 *
 * The same shape the duel uses, and for the same reason: a room is driven by
 * incoming messages, and a room nobody is talking to still has to notice a
 * grace window expiring. Twenty times coarser than a tick.
 */
export const OPEN_WORLD_WATCHDOG_INTERVAL_MS = 1_000;

/**
 * How often the room writes itself down while it is merely being played.
 *
 * Critical transitions - a seat taken, a seat dropped, a death, a room letting
 * go - are written before they are announced and do not wait for this.
 */
export const OPEN_WORLD_PERSIST_INTERVAL_MS = 1_000;

/**
 * **Three minutes, and it is the whole disconnection rule** (in this gate).
 *
 * A dangerous quadrant does not pause and does not protect: the hull stays
 * where it was, visible, solid and killable, and the clock runs. Coming back
 * inside this window resumes exactly the same state. Not coming back produces
 * an authoritative death.
 *
 * Measured against the room's own clock, never `Date.now()` - see
 * {@link OPEN_WORLD_MAX_OBSERVED_MS} for why.
 */
export const OPEN_WORLD_RECONNECT_GRACE_MS = 3 * 60 * 1_000;

/**
 * How long a wreck stays in the room after its death has been published.
 *
 * Long enough that every connection in range gets the death in a frame, short
 * enough that a quadrant does not accumulate hulls nobody is flying. When it
 * expires the chair is released.
 *
 * **This is the seam where a loot container goes.** The gate says a death
 * produces an authoritative event and no container and no reward yet, so what
 * happens here today is that the entry is removed and nothing takes its place.
 * A later gate replaces the removal with a container; nothing else about the
 * death has to move.
 *
 * Provisional.
 */
export const OPEN_WORLD_DEATH_RETENTION_TICKS = 40;

/**
 * How long a room with nobody in it waits before letting itself go.
 *
 * A room is born when somebody arrives at a quadrant and should stop existing
 * when the last chair is gone - a hundred quadrants of idle rooms is a hundred
 * processes waiting for nobody, which is the shape ADR 0066 refused. The linger
 * covers the ordinary case of a player dropping and immediately reconnecting
 * while the last chair is being released.
 */
export const OPEN_WORLD_ROOM_LINGER_MS = 30_000;

/* -------------------------------------------------------------------------- */
/*  The hull                                                                  */
/* -------------------------------------------------------------------------- */

/** How far the reference hull travels in one tick. The duel's number, read. */
export const OPEN_WORLD_SHIP_TRAVEL_MU_PER_TICK = PVP_SHIP_SPEED_MU_PER_TICK;

/** The reference hull's radius. Bigger lives scale it, by the shared curve. */
export const OPEN_WORLD_SHIP_RADIUS_MU = PVP_SHIP_RADIUS_MU;

/**
 * The hull the size curve is measured against: the base ship's maximum life.
 *
 * The neutral constant from `hull/base-health.ts`, which is the same value the
 * duel's `PVP_MAX_HEALTH_MILLI` already is. Taken from the leaf rather than
 * from the duel because it describes a SHIP and not a match.
 */
export const OPEN_WORLD_REFERENCE_MAX_HEALTH_MILLI = SHIP_BASE_MAX_HEALTH_MILLI;

/* -------------------------------------------------------------------------- */
/*  The ordinary cannon                                                       */
/* -------------------------------------------------------------------------- */

export const OPEN_WORLD_PROJECTILE_SPEED_MU_PER_TICK = PVP_PROJECTILE_SPEED_MU_PER_TICK;
export const OPEN_WORLD_PROJECTILE_RADIUS_MU = PVP_PROJECTILE_RADIUS_MU;
export const OPEN_WORLD_PROJECTILE_LIFETIME_TICKS = PVP_PROJECTILE_LIFETIME_TICKS;

/**
 * How far a shot is stepped WITHIN one tick before it is tested for hits.
 *
 * A shot covers more ground per tick than a hull is wide, so a single
 * end-of-tick test would let it pass clean through a ship - the oldest bug in
 * projectile collision. The duel's answer, read rather than re-derived.
 */
export const OPEN_WORLD_PROJECTILE_SUBSTEPS = PVP_PROJECTILE_SUBSTEPS;

/** One ordinary projectile's whole damage, before any percentage. */
export const OPEN_WORLD_PROJECTILE_DAMAGE = PVP_PROJECTILE_DAMAGE;

/** The base interval between volleys, in milli-ticks. Jewels shorten it. */
export const OPEN_WORLD_FIRE_INTERVAL_MILLI_TICKS = PVP_FIRE_INTERVAL_TICKS * MILLI_TICKS_PER_TICK;

/*
 * **O teto de corpos em voo saiu daqui** (gate V28I.AG).
 *
 * Era `OPEN_WORLD_MAX_PROJECTILES = 4096`, e a auditoria mostrou o que ele
 * realmente era: um orçamento OPERACIONAL, e nem sequer um do tamanho certo.
 * Nada no formato, no protocolo, no descodificador ou na instantânea exigia um
 * comprimento máximo, e a mecânica que os gates seguintes aprovaram pede duas
 * ordens de grandeza mais do que ele dava — cento e vinte e oito pilotos a vinte
 * salvas por segundo com vinte e oito corpos cada, a três segundos de voo, são
 * mais de duzentos mil corpos.
 *
 * Um número desses no equilíbrio da sala fazia o jogo produzir MENOS trabalho
 * do que a configuração pedia, e um ensaio nessas condições aprovaria um
 * candidato que nunca correu.
 *
 * Passou a ser `OPEN_WORLD_MAX_LIVE_PROJECTILES_PER_QUADRANT`: uma política da
 * sala, configurável, sem valor por omissão, e com toda a saturação contada.
 * Não foi substituído por um limite estrutural novo, porque nunca houve nenhum a
 * substituir.
 */

/* -------------------------------------------------------------------------- */
/*  What the fourteen jewels need, read from the duel and never rewritten      */
/* -------------------------------------------------------------------------- */

/**
 * **Every number the jewels use is IMPORTED, and none of them is decided here.**
 *
 * The Atomic Bomb's speed, body, proximity and blast; the Missile's speed,
 * lifetime, body, capture radius and ceiling; the Poison's interval. Each one is
 * the duel's, read through this module's names so the room reads in its own
 * vocabulary and there is still exactly one place the value lives.
 *
 * The chances, the magnitudes, the orbits, the contact clocks and the closed
 * tables are not here at all: they belong to `damage/`, which both simulations
 * already share. This file only re-exports the GEOMETRY and the CADENCE the
 * entities need in order to fly through a quadrant.
 */

/** The bomb's speed, in basis points of the shot it replaced. */
export const OPEN_WORLD_BOMB_SPEED_BPS = PVP_BOMB_SPEED_BPS;
/** The bomb's body: what meets a wall, and what a client draws. */
export const OPEN_WORLD_BOMB_BODY_RADIUS_MU = PVP_BOMB_BODY_RADIUS_MU;
/** How close a hull has to be for the bomb to go off. Wider than a shot. */
export const OPEN_WORLD_BOMB_PROXIMITY_RADIUS_MU = PVP_BOMB_PROXIMITY_RADIUS_MU;
/** The blast's MECHANICAL radius: what decided who was caught. */
export const OPEN_WORLD_BOMB_BLAST_RADIUS_MU = PVP_BOMB_BLAST_RADIUS_MU;

export const OPEN_WORLD_MISSILE_SPEED_MU_PER_TICK = PVP_MISSILE_SPEED_MU_PER_TICK;
export const OPEN_WORLD_MISSILE_LIFETIME_TICKS = PVP_MISSILE_LIFETIME_TICKS;
export const OPEN_WORLD_MISSILE_HIT_RADIUS_MU = PVP_MISSILE_HIT_RADIUS_MU;
export const OPEN_WORLD_MISSILE_BODY_RADIUS_MU = PVP_MISSILE_BODY_RADIUS_MU;
/*
 * **O teto próprio dos mísseis saiu daqui** (gate V28I.AH).
 *
 * Era `OPEN_WORLD_MAX_MISSILES`, e o que ele fazia ao ser atingido era
 * `missiles.shift()` — apagar em silêncio o míssil mais antigo EM VOO. Um corpo
 * que já existia, que já estava a ser desenhado e que já podia colidir
 * desaparecia a meio por causa de um limite interno.
 *
 * Os mísseis passaram para o orçamento comum de corpos ofensivos, onde a vaga é
 * reservada ANTES do nascimento: a salva que sorteia um Míssil reserva
 * `largura + 1`, e se isso não couber a salva inteira não nasce e fica contada.
 * Nada que já voa é tocado.
 */

/**
 * **How far a missile can reach in its whole life**, in milli-units.
 *
 * Derived - speed times lifetime - and it is the one missile number a quadrant
 * needs that an arena never did. A duel's arena is smaller than this, so every
 * hull in it is reachable by construction; a quadrant is seven times wider, and
 * a target further away than this cannot be caught however well the missile
 * steers.
 *
 * It is also what keeps the steering EXACT. `pvpMissileAimPoint` squares the
 * missile-to-target separation, and across a whole quadrant that leaves the
 * exact-integer range. Bounding the candidates to this bounds the arithmetic:
 * the widest square that survives is about 2.5e14, against a safe ceiling of
 * 9.0e15.
 */
export const OPEN_WORLD_MISSILE_REACH_MU =
  OPEN_WORLD_MISSILE_SPEED_MU_PER_TICK * OPEN_WORLD_MISSILE_LIFETIME_TICKS;

/** Ticks between two poison instalments. The duel's second, in this tick rate. */
export const OPEN_WORLD_POISON_TICK_INTERVAL_TICKS = PVP_POISON_TICK_INTERVAL_TICKS;

/* -------------------------------------------------------------------------- */
/*  Spawning                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The side of the spawn lattice: eight by eight, sixty-four points.
 *
 * ## Why a lattice and not a draw
 *
 * Because there is no authoritative random number generator in this room, and
 * inventing one to choose a spawn would be inventing state the snapshot has to
 * carry and two re-simulations have to agree on. A lattice walked by an
 * ordinal is exactly reproducible from the ordinal alone, which is one integer.
 *
 * The ordinal keeps climbing and wraps past sixty-four, so two hulls can share
 * a point in a very busy quadrant. That is handled by the movement's own
 * push-out, which resolves an exact overlap deterministically and always in the
 * same direction - it is not left to chance either.
 *
 * The points sit inside the content box, which already keeps its distance from
 * every edge, so nobody is born touching a wall.
 *
 * Provisional: where a pilot ARRIVES is a traversal question, and traversal is
 * a later gate. When it exists, an arrival will name its own edge and this
 * lattice becomes the answer for the case that has no edge to name.
 */
export const OPEN_WORLD_SPAWN_LATTICE_SIDE = 8;

/**
 * The proof, at module load, that this room's arithmetic stays exact.
 *
 * ## What could go wrong, and where
 *
 * A quadrant is 81 600 000 milli-units a side, and `dx * dx + dy * dy` across
 * one is about 1.3e16 - past `Number.MAX_SAFE_INTEGER`. Every distance question
 * this room asks therefore refuses to square anything until both axes are
 * already known to be short: the interest area tests each axis first, the hit
 * test does, and `motion/hull.ts` does. This asserts that the widest radius any
 * of them can survive with is still exact, so a future change that widens the
 * interest area is a loud failure at startup rather than a silently wrong
 * comparison in one frame of one fight.
 */
/* -------------------------------------------------------------------------- */
/*  Cometas — EQUILÍBRIO PROVISÓRIO (gate V28I.I)                             */
/* -------------------------------------------------------------------------- */

/**
 * **Tudo o que se segue é provisório e está aqui por isso.**
 *
 * O contrato aprovado decidiu quatro números — três a nove cometas por
 * quadrante, trinta por cento de joia, trinta minutos de reconstrução, uma hora
 * de renascimento — e não decidiu mais nenhum. Quantas partes, quanta vida,
 * que tamanho, quanto ouro por dano, quando o núcleo se expõe: nada disso foi
 * calibrado, e inventá-lo espalhado pelo código seria transformar uma escolha
 * por decidir num facto difícil de encontrar.
 *
 * Por isso estão TODOS aqui, com unidade e razão, e estão registados em
 * `docs/OPEN_QUESTIONS.md` como equilíbrio pendente do benchmark final.
 *
 * A regra que os escolheu: **conservadores**. Um cometa deve custar trabalho
 * visível sem ser uma parede, e o ouro que ele paga deve ser notado sem
 * competir com o que ainda ninguém desenhou.
 */

/** Quantas partes um cometa pode ter, inclusive. Cabem numa máscara de 32 bits. */
export const OPEN_WORLD_COMET_PARTS_MIN = 4;
export const OPEN_WORLD_COMET_PARTS_MAX = 9;

/**
 * A vida de UMA parte, em milli.
 *
 * Referida à vida da nave de referência e não escrita solta: uma parte vale
 * metade de um casco base, portanto um cometa de quatro partes custa dois
 * cascos e um de nove custa quatro e meio. É a escala que faz um cometa ser
 * trabalho e não uma parede.
 */
export const OPEN_WORLD_COMET_PART_HEALTH_MILLI = OPEN_WORLD_REFERENCE_MAX_HEALTH_MILLI / 2n;

/**
 * A vida do núcleo, em milli. Uma parte e meia.
 *
 * Mais do que uma parte de propósito: o núcleo é a última coisa, e chegar a ele
 * não pode ser a mesma coisa que partir mais um pedaço.
 */
export const OPEN_WORLD_COMET_CORE_HEALTH_MILLI = (OPEN_WORLD_REFERENCE_MAX_HEALTH_MILLI * 3n) / 4n;

/**
 * Quantas partes têm de cair para o núcleo ficar exposto, em pontos base do
 * total. **Setenta por cento.**
 *
 * Em pontos base e não numa contagem, porque o número de partes varia: setenta
 * por cento de quatro são três, e de nove são sete. A regra é a mesma nos dois.
 */
export const OPEN_WORLD_COMET_EXPOSURE_BPS = 7_000;

/**
 * O raio do corpo, em milli-unidades.
 *
 * Quatro cascos de referência de raio — grande o suficiente para as partes se
 * distinguirem e para uma explosão apanhar várias, pequeno o suficiente para
 * não ocupar o ecrã.
 */
export const OPEN_WORLD_COMET_BODY_RADIUS_MU = OPEN_WORLD_SHIP_RADIUS_MU * 4;

/** O raio de UMA parte, em milli-unidades. É o que uma bala tem de tocar. */
export const OPEN_WORLD_COMET_PART_RADIUS_MU = OPEN_WORLD_SHIP_RADIUS_MU;

/**
 * Quanto ouro de campo um milésimo de dano paga, em micro-ouro.
 *
 * A conta é `ouro = dano_milli * RAZÃO / 1_000_000`, exata em `bigint`, e a
 * fração que sobra fica num acumulador por atacante — mil golpes pequenos não
 * pagam zero. Com esta razão, partir um cometa de quatro partes rende cerca de
 * duzentos de ouro de campo.
 */
export const OPEN_WORLD_COMET_GOLD_MICRO_PER_DAMAGE_MILLI = 1_000n;

/**
 * De quanto em quanto tempo o ouro acumulado é fechado num lote e mandado à
 * autoridade económica, em milissegundos.
 *
 * **Nunca uma escrita por partícula.** Cinco segundos junta o ouro de uma
 * troca inteira numa transação; o lote também fecha quando o cometa se esgota,
 * para que a última parcela não fique à espera de um relógio.
 */
export const OPEN_WORLD_COMET_GOLD_BATCH_MS = 5_000;

/* -------------------------------------------------------------------------- */
/*  Planetas — EQUILÍBRIO PROVISÓRIO (gate V28I.J)                            */
/* -------------------------------------------------------------------------- */

/**
 * **Provisório, como o dos cometas, e aqui pela mesma razão.**
 *
 * O contrato aprovado decidiu quatro coisas — um a três planetas por quadrante,
 * um a três núcleos por planeta, trinta minutos de reconstrução e trinta de
 * geração nova — e mais nada. Quantas placas, quanta vida, que tamanho, quanto
 * ouro esconde uma placa e com que frequência: nada disso foi calibrado.
 *
 * Estão todos aqui, com unidade e razão, e registados em
 * `docs/OPEN_QUESTIONS.md` como equilíbrio pendente do benchmark final.
 *
 * A regra que os escolheu, outra vez: **conservadores**, e referidos ao que já
 * existe. Um planeta é um cometa maior — mais placas, mais vida, mais raio —
 * porque essa é a única relação que o contrato afirma sem números.
 */

/** Quantas placas um planeta pode ter, inclusive. Mais do que um cometa. */
export const OPEN_WORLD_PLANET_PARTS_MIN = 8;
export const OPEN_WORLD_PLANET_PARTS_MAX = 14;

/**
 * A vida de UMA placa, em milli.
 *
 * Um casco de referência inteiro — o dobro de uma parte de cometa. Um planeta
 * de oito placas custa oito cascos, e um de catorze custa catorze: é trabalho
 * de grupo ou de paciência, que é o que "corpo maior" tem de significar em
 * tempo e não só em pixels.
 */
export const OPEN_WORLD_PLANET_PART_HEALTH_MILLI = OPEN_WORLD_REFERENCE_MAX_HEALTH_MILLI;

/**
 * A vida de UM núcleo, em milli.
 *
 * Uma placa e meia. Mais do que uma placa porque chegar a um núcleo não pode
 * ser a mesma coisa que partir mais uma peça do exterior, e menos do que duas
 * porque há até três deles.
 */
export const OPEN_WORLD_PLANET_CORE_HEALTH_MILLI =
  (OPEN_WORLD_REFERENCE_MAX_HEALTH_MILLI * 3n) / 2n;

/**
 * O raio do corpo, em milli-unidades. **Seis raios de casco.**
 *
 * Metade maior do que um cometa (quatro), que é o que faz a silhueta ser
 * distinguível de longe sem tomar o ecrã.
 */
export const OPEN_WORLD_PLANET_BODY_RADIUS_MU = OPEN_WORLD_SHIP_RADIUS_MU * 6;

/** O raio de UMA placa. Maior do que a parte de um cometa, como o corpo. */
export const OPEN_WORLD_PLANET_PART_RADIUS_MU = (OPEN_WORLD_SHIP_RADIUS_MU * 3) / 2;

/**
 * A chance de uma placa esconder ouro, em pontos base. **Quarenta por cento.**
 *
 * Provisória: ninguém decidiu com que frequência uma placa paga. Quarenta
 * significa que um planeta de dez placas paga quatro vezes em média, o que é
 * frequente o suficiente para se notar e raro o suficiente para ser uma aposta.
 */
export const OPEN_WORLD_PLANET_PART_GOLD_CHANCE_BPS = 4_000;

/**
 * Quanto ouro de campo uma placa com ouro esconde, em unidades inteiras.
 *
 * Um intervalo, sorteado por placa. Referido ao que um cometa paga: partir um
 * cometa de quatro partes rende cerca de duzentos, portanto uma placa que paga
 * vale entre um oitavo e meio disso — e um planeta inteiro rende mais do que um
 * cometa por ter mais placas, não por cada uma valer mais.
 */
export const OPEN_WORLD_PLANET_PART_GOLD_MIN = 25n;
export const OPEN_WORLD_PLANET_PART_GOLD_MAX = 100n;

/**
 * Quantas placas cobrem UM núcleo.
 *
 * Três. É o que faz um núcleo aparecer **progressivamente**: as placas que o
 * cobrem são escolhidas pela mesma aritmética determinística que as coloca, e
 * enquanto uma delas estiver de pé o núcleo continua escondido e protegido.
 *
 * Três e não todas: com todas, o último núcleo só apareceria com o corpo
 * inteiro no chão, e "aparecem progressivamente" deixaria de ser verdade.
 */
export const OPEN_WORLD_PLANET_CORE_COVER_PARTS = 3;

/**
 * **A espera entre duas tentativas de pagar o ouro de uma placa** (gate V28I.BZ).
 *
 * Uma falha transitória — a base não respondeu, a ligação caiu — não se repete a
 * cada tique: a sala espera um segundo, depois o dobro de cada vez, e nunca mais
 * de trinta. A dívida continua lá, com o mesmo dono e a mesma chave, e a drenagem
 * de uma extração ou de uma morte espera por ela. Uma política de entrega, e não
 * um valor de jogo.
 */
export const OPEN_WORLD_PLANET_GOLD_RETRY_MIN_MS = 1_000;
export const OPEN_WORLD_PLANET_GOLD_RETRY_MAX_MS = 30_000;

/* -------------------------------------------------------------------------- */
/*  Mobs — o que vem do catálogo, e o que é PROVISÓRIO (gate V28I.K)          */
/* -------------------------------------------------------------------------- */

/**
 * **O que NÃO está aqui, e é o mais importante desta secção.**
 *
 * A vida de um mob, a escala dele, o raio de colisão, o dano que ele faz e o
 * ouro que ele paga **não são constantes deste ficheiro**. Eles vêm do catálogo
 * da caça — `mobHealthForMap`, `mobScaleBpsForMap`, `mobDisplaySizeForMap`,
 * `HUNT_BALANCE.mobs.hitRadiusFraction`, `mobLeakDamage` e a fração de ouro —
 * e são lidos de lá em cada uso. Copiá-los para aqui teria criado uma segunda
 * tabela a divergir da primeira no dia em que alguém afinasse uma delas.
 *
 * O que está aqui é o que a caça **não tem porque não precisa**: ela é um
 * arcade de descida onde nada persegue nada. Um quadrante aberto precisa de
 * decidir a que distância um mob repara em alguém, até onde o segue, e para
 * onde volta — e nada disso foi calibrado por ninguém.
 *
 * Todos provisórios, registados em `docs/OPEN_QUESTIONS.md`, e escolhidos
 * conservadoramente: um mob deve ser um obstáculo, não uma armadilha, e
 * desistir dele tem de ser sempre possível.
 */

/*
 * **A deteção e a trela de um mob vivem em `OPEN_WORLD_AGGRO_DETECTION_MU` e
 * `OPEN_WORLD_AGGRO_LEASH_MU`** (gate V28I.AN.1), no fim deste ficheiro.
 *
 * Eram duas constantes daqui, derivadas da área de interesse. Um boss tinha
 * outras duas, com exatamente os mesmos números dentro — e duas cópias do mesmo
 * número são o sítio onde uma divergência começa. Agora é uma derivação só,
 * ancorada no que um jogador VÊ em vez de numa distância de voo.
 */

/**
 * O raio da região de origem, em milli-unidades.
 *
 * Um mob sem alvo fica dentro disto, à volta da vaga onde nasceu. Oito raios de
 * casco: espaço para ele não parecer pregado, e pequeno o suficiente para a
 * vaga continuar a ser um sítio.
 */
export const OPEN_WORLD_MOB_HOME_RADIUS_MU = OPEN_WORLD_SHIP_RADIUS_MU * 8;

/**
 * Quanto um mob anda por tique, em milli-unidades.
 *
 * **Setenta por cento da nave de referência.** Um mob mais rápido do que uma
 * nave tornaria a fuga impossível, e a fuga tem de ser sempre possível: um
 * quadrante perigoso não pode ter armadilhas de que não se sai.
 *
 * Não é a velocidade de descida da caça: aquela são 46 unidades por segundo num
 * ecrã de 1280 por 720, e este quadrante tem 81 600 unidades de lado. Trazer o
 * número seria trazer uma proporção que não existe entre os dois sítios.
 */
export const OPEN_WORLD_MOB_TRAVEL_MU_PER_TICK = (OPEN_WORLD_SHIP_TRAVEL_MU_PER_TICK * 7) / 10;

/**
 * Quantos tiques um mob mantém o alvo antes de reconsiderar.
 *
 * Um segundo. Sem isto, dois cascos a igual distância trocariam de vez a cada
 * tique e o mob ficaria a tremer entre os dois — que é a "troca aleatória de
 * alvo a cada tique" que o contrato proíbe. Ele reconsidera de segundo a
 * segundo, e larga imediatamente um alvo que deixou de ser elegível.
 */
export const OPEN_WORLD_MOB_TARGET_HOLD_TICKS = 20;

/**
 * De quanto em quanto tempo um mob pode magoar por contacto, em milli-tiques.
 *
 * Um segundo. O dano em si é o canónico — `mobLeakDamage` — e o que é
 * provisório é a CADÊNCIA: na caça um mob toca uma vez e desaparece, portanto
 * não há uma cadência canónica de contacto para reutilizar.
 *
 * Em milli-tiques como a cadência de um casco, para o acumulador ser exato e
 * não perder frações entre tiques.
 */
export const OPEN_WORLD_MOB_CONTACT_INTERVAL_MILLI_TICKS = 20 * MILLI_TICKS_PER_TICK;

/**
 * O espaçamento mínimo entre um mob a nascer e o que já lá está, em
 * milli-unidades.
 *
 * Quatro raios de casco. Chega para dois mobs não nascerem um dentro do outro e
 * para nenhum nascer em cima de uma nave — e a busca que o usa é limitada, com
 * o nascimento a ser ADIADO quando não encontra sítio, nunca sorteado até
 * acertar.
 */
export const OPEN_WORLD_MOB_SPAWN_SPACING_MU = OPEN_WORLD_SHIP_RADIUS_MU * 4;

/** Quantas posições a busca de nascimento tenta antes de adiar. Determinística. */
export const OPEN_WORLD_MOB_SPAWN_ATTEMPTS = 8;

/* -------------------------------------------------------------------------- */
/*  Bosses — o que vem do catálogo, e o que é PROVISÓRIO (gate V28I.L)        */
/* -------------------------------------------------------------------------- */

/**
 * **O que NÃO está aqui, outra vez, e continua a ser o mais importante.**
 *
 * O tipo de um boss, o tamanho dele, a escala, o raio, a vida e a distribuição
 * de tamanhos **não são constantes deste ficheiro**. Vêm do catálogo:
 * `bossVisualScaleBps` e a tabela de tiers (`shared/src/boss/tier.ts`),
 * `bossHealth` e `HUNT_BALANCE.boss` (`hunt/balance.ts`), `mobScaleBpsForMap`
 * e `mobDisplaySizeForMap`. O que este gate acrescenta por cima é uma regra
 * exata — a vida vezes três — e nada mais.
 *
 * O que está aqui é o que a caça **não tem porque não precisa**: o boss dela
 * desce em linha reta, não persegue ninguém e nunca ataca — cruzar a linha de
 * baixo é a ofensiva inteira dele. Um boss de quadrante tem de reparar em
 * alguém, segui-lo, alcançá-lo e feri-lo, e nada disso existe para ser copiado.
 *
 * Todos provisórios, registados em `docs/OPEN_QUESTIONS.md`.
 */

/*
 * **Um boss usa a MESMA deteção e a MESMA trela de um alienígena**
 * (gate V28I.AN.1): `OPEN_WORLD_AGGRO_DETECTION_MU` e
 * `OPEN_WORLD_AGGRO_LEASH_MU`, no fim deste ficheiro.
 *
 * A razão que aqui estava escrita continua a valer, e é a mais dura das duas:
 * um boss anuncia as magias dele antes de elas ferirem, e o anúncio só serve se
 * quem o recebe já estiver a vê-lo. Ancorada no ecrã, ela passa a ser verdade.
 */

/**
 * Até onde um boss se afasta da vaga dele, em milli-unidades.
 *
 * Maior do que a de um mob — um boss é um sítio a que se vai, e a região de
 * combate dele é o que decide se ele está abandonado.
 */
export const OPEN_WORLD_BOSS_HOME_RADIUS_MU = OPEN_WORLD_SHIP_RADIUS_MU * 16;

/**
 * Quanto um boss anda por tique, em milli-unidades.
 *
 * **A fração canónica**, lida de `HUNT_BALANCE.boss.descentSpeedFraction`: na
 * caça um boss desce a 35% da velocidade de um alienígena, e aqui ele persegue à
 * mesma proporção da velocidade que um alienígena tem neste mundo. Um boss é
 * lento de propósito — fugir tem de ser sempre possível — e o preço de o
 * enfrentar é ficar perto dele.
 */
export const OPEN_WORLD_BOSS_TRAVEL_MU_PER_TICK = Math.max(
  1,
  Math.round(OPEN_WORLD_MOB_TRAVEL_MU_PER_TICK * HUNT_BALANCE.boss.descentSpeedFraction),
);

/** Quantos tiques um boss segura o alvo antes de reconsiderar. Como os mobs. */
export const OPEN_WORLD_BOSS_TARGET_HOLD_TICKS = 20;

/**
 * De quanto em quanto tempo um contacto de corpo a corpo volta a ferir.
 *
 * Duas vezes o intervalo de um mob: um boss bate mais forte e mais devagar, e a
 * janela mais larga é o que dá tempo a alguém para se afastar entre golpes.
 */
export const OPEN_WORLD_BOSS_CONTACT_INTERVAL_MILLI_TICKS = 40 * MILLI_TICKS_PER_TICK;

/**
 * **O ataque base de um boss, em basis points da vida máxima da vítima.**
 *
 * O número mais provisório deste ficheiro, e o único verdadeiramente inventado:
 * **a caça não tem um ataque de boss para ler**. O boss dela nunca ataca — ele
 * desce, e cruzar a linha de baixo mata independentemente da vida. Não há
 * fração, coeficiente nem fórmula canónica para reaproveitar, e escolher uma é
 * o que este gate teve de fazer.
 *
 * Doze por cento da vida máxima de quem apanha, por golpe, no tier 1. É a base
 * de TUDO o que um boss faz: o contacto de um corpo a corpo e cada etapa de
 * cada uma das vinte habilidades saem daqui, pelos basis points do perfil dela.
 * Afinar um boss é afinar este número.
 */
export const OPEN_WORLD_BOSS_ATTACK_BPS = 1_200;

/*
 * **A conjuração provisória saiu daqui no gate V28I.BG.**
 *
 * Viviam neste sítio o intervalo entre magias, a carga de 30 tiques, o recuo do
 * conjurador, e o alcance, a velocidade e a vida da Esfera Ígnea de um boss.
 * Todos serviam UMA habilidade partilhada por todos os conjuradores enquanto os
 * kits definitivos não existiam. Os vinte kits existem, cada um traz o seu
 * telegraph, o seu alcance e o seu arrefecimento, e uma constante que ninguém
 * lê é um número que alguém vai acreditar que ainda decide alguma coisa.
 */

/**
 * A proporção da Esfera Ígnea e da Lança Solar **do jogador** no mundo aberto.
 *
 * As proporções canónicas do duelo (`PVP_FIREBALL_REACH_BPS`,
 * `PVP_SOLAR_LANCE_REACH_BPS` e `PVP_SOLAR_LANCE_WIDTH_BPS`), medidas no raio do
 * casco de quem as ativa. A duração e o intervalo das parcelas vêm de
 * `cardBalance('card_solar_lance')` e não estão escritos aqui: são a carta, e a
 * carta é canónica.
 *
 * O prefixo `BOSS` é histórico — um boss partilhou estas três até o kit dele
 * existir — e o valor não mudou com a remoção.
 */
export const OPEN_WORLD_BOSS_SPHERE_REACH_BPS = PVP_FIREBALL_REACH_BPS;
export const OPEN_WORLD_BOSS_LANCE_REACH_BPS = PVP_SOLAR_LANCE_REACH_BPS;
export const OPEN_WORLD_BOSS_LANCE_WIDTH_BPS = PVP_SOLAR_LANCE_WIDTH_BPS;

/** Que espaço um boss precisa à volta da vaga para nascer, em milli-unidades. */
export const OPEN_WORLD_BOSS_SPAWN_SPACING_MU = OPEN_WORLD_SHIP_RADIUS_MU * 8;

/** Quantas posições a busca de nascimento tenta antes de adiar. Determinística. */
export const OPEN_WORLD_BOSS_SPAWN_ATTEMPTS = 8;

/* -------------------------------------------------------------------------- */
/*  Os golpes físicos dos seis corpo a corpo (gate V28I.BA)                    */
/* -------------------------------------------------------------------------- */

/**
 * **De onde sai cada número destes.**
 *
 * Nada aqui é um valor solto. Cada grandeza é uma relação com uma fonte que já
 * existia, e a relação está escrita ao lado dela:
 *
 * - **dano** — basis points do ataque base do boss (`openWorldBossAttackMilli`),
 *   que por sua vez é `OPEN_WORLD_BOSS_ATTACK_BPS` sobre a vida base de um
 *   casco. Nunca sobre a vida da vítima;
 * - **distâncias** — múltiplos do raio REAL do boss (`boss.radiusMu`) e do raio
 *   de um casco (`OPEN_WORLD_SHIP_RADIUS_MU`), nunca do tamanho do sprite;
 * - **deslocamentos** — múltiplos de `OPEN_WORLD_BOSS_TRAVEL_MU_PER_TICK`;
 * - **durações** — números inteiros de tiques de `OPEN_WORLD_TICK_MS`;
 * - **empurrão** — frações de `OPEN_WORLD_BOMB_KNOCKBACK_MU`, entregues às
 *   funções canónicas do duelo;
 * - **cadência das brasas** — `PVP_POISON_TICK_INTERVAL_TICKS`, a única cadência
 *   canónica de dano persistente deste repositório.
 *
 * Todos provisórios, e registados em `docs/OPEN_QUESTIONS.md`.
 */

/** Quanto tempo um corpo a corpo persegue antes de parar de emboscada. */
export const OPEN_WORLD_BOSS_AMBUSH_INTERVAL_TICKS = 60;

/** Quantos tiques ele fica parado nessa emboscada. */
export const OPEN_WORLD_BOSS_AMBUSH_TICKS = 10;

/**
 * **A forma de uma etapa de golpe**, e o que ela cobra.
 *
 * `ticks` é a duração; `damageBps` é o dano em basis points do ataque base, e
 * **zero é uma etapa sem dano** — o intervalo legível entre duas maças e a
 * janela de fuga entre a segunda e a terceira varrida são etapas de pleno
 * direito, com duração publicada, e não pausas escondidas.
 */
export interface OpenWorldBossStrikeStage {
  readonly ticks: number;
  readonly damageBps: number;
  /** O meio-ângulo, em basis points da tangente. Zero quando não é um sector. */
  readonly sectorTanBps: number;
  /**
   * **Esta etapa executa-se mesmo sem dano** (gate V28I.BE).
   *
   * Uma etapa sem dano é normalmente uma janela de fuga. Estas não são: elas
   * fazem outra coisa — puxam um casco, deixam uma zona no chão, fazem nascer
   * uma bala — e a simulação tem de as ver. Por etapa e não por habilidade,
   * porque o Pó tem quatro etapas e só a primeira age.
   */
  readonly acts?: boolean;
  /**
   * **Esta etapa publica a geometria mesmo sem dano.**
   *
   * O aviso de uma mandíbula, o pó no chão e as linhas do puxão têm de ser
   * desenhados; uma janela de fuga não. Sem isto, `rr` seria zero e o cliente
   * não teria o que mostrar exactamente enquanto o aviso importa.
   */
  readonly announces?: boolean;
  /**
   * **O alcance PRÓPRIO desta etapa**, em raios de casco, quando ele difere do
   * da habilidade. O colapso da Fome cobra num raio muito menor do que o do
   * puxão, e é isso que faz fugir valer a pena.
   */
  readonly reachHullRadii?: number;
}

/** O perfil completo de um golpe. Uma linha por habilidade, e mais nenhuma. */
export interface OpenWorldBossStrikeProfile {
  /** O gatilho, em raios de casco ALÉM do corpo do boss. */
  readonly triggerHullRadii: number;
  /** O alcance da geometria, em raios de casco além do corpo do boss. */
  readonly reachHullRadii: number;
  /** A meia-largura de uma cápsula, em raios de casco. Zero se não for cápsula. */
  readonly halfWidthHullRadii: number;
  readonly windupTicks: number;
  readonly telegraphTicks: number;
  readonly stages: readonly OpenWorldBossStrikeStage[];
  readonly recoveryTicks: number;
  readonly cooldownTicks: number;
  /** O empurrão por acerto, em milésimos do empurrão canónico da bomba. */
  readonly knockbackPermille: number;
  /**
   * **A origem congelada é a posição do ALVO, e não a do boss** (gate V28I.BC).
   *
   * A Coroa e o Selo nascem debaixo de quem eles perseguem: é isso que faz a
   * resposta ser sair do sítio onde se está, em vez de sair da frente do boss.
   * Ausente vale `false`, que é o que as dez outras habilidades sempre foram.
   */
  readonly originIsTarget?: boolean;
  /**
   * **O alcance soma o corpo do boss.**
   *
   * Verdadeiro para tudo o que sai DELE — um cone, um feixe, um disco de
   * corrente. Falso para o que é medido no sítio onde nasce: um anel à volta do
   * alvo tem o raio dele e nada mais, e uma faixa que atravessa o boss mede-se
   * a partir do centro dele. Ausente vale `true`.
   */
  readonly reachIncludesBody?: boolean;
  /**
   * **A banda em que este conjurador se mantém**, em raios de casco além do
   * corpo. Ausentes valem 16 e 24, que é a banda partilhada. O Coro precisa de
   * espaço para as ondas dele e usa 20 e 28.
   */
  readonly bandNearHullRadii?: number;
  readonly bandFarHullRadii?: number;
  /**
   * **Quantos corpos ofensivos esta habilidade RESERVA antes de começar.**
   *
   * Ausente é "nenhum". O Enxame reserva oito e o Fogo Rotativo vinte e cinco:
   * as vagas passam a contar no orçamento no instante da reserva, e cada corpo
   * que nasce gasta uma sem aumentar o total comprometido.
   */
  readonly reserveBodies?: number;
}

/**
 * **Meio-ângulo de 45 graus**, em basis points da tangente: `tan 45° = 1`.
 *
 * O teste é `|cross| x 10 000 <= dot x bps`, inteiro e igual em qualquer motor.
 * `Math.atan2` é definido pela implementação e Node e Chromium diferem no
 * último bit — um cone decidido por ângulo dessincronizaria a re-simulação.
 */
export const OPEN_WORLD_BOSS_SECTOR_45_TAN_BPS = 10_000;
/** `tan 30° = 0,57735…`, truncado ao mesmo denominador. */
export const OPEN_WORLD_BOSS_SECTOR_30_TAN_BPS = 5_774;
/** `tan 60° = 1,73205…`, no mesmo denominador. */
export const OPEN_WORLD_BOSS_SECTOR_60_TAN_BPS = 17_321;

/**
 * **A velocidade da estocada do Ferrão**, em múltiplos da velocidade do boss.
 *
 * Quinze: 62 475 mu por tique, cerca de três vezes e meia a velocidade de um
 * casco. É o que faz a estocada ser uma ameaça depois de o corredor estar
 * desenhado — e o corredor é estreito o suficiente para um passo lateral sair
 * dele durante os doze tiques do aviso.
 */
export const OPEN_WORLD_BOSS_LUNGE_SPEED_FACTOR = 15;

/** O recuo antes da estocada. A velocidade normal, para trás. */
export const OPEN_WORLD_BOSS_LUNGE_RETREAT_FACTOR = 1;

/** A investida do Rugido, em múltiplos da velocidade do boss. */
export const OPEN_WORLD_BOSS_CHARGE_SPEED_FACTOR = 8;

/** Quantos tiques a investida acelera antes de travar. */
export const OPEN_WORLD_BOSS_CHARGE_TICKS = 20;

/** Quantos tiques ela trava, em desaceleração linear. */
export const OPEN_WORLD_BOSS_BRAKE_TICKS = 6;

/** A que fração da velocidade normal o Volteio persegue. Metade. */
export const OPEN_WORLD_BOSS_WHIRL_TRAVEL_DIVISOR = 2;

/** Quantos tiques as Duas Correntes se aproximam antes de recuar. */
export const OPEN_WORLD_BOSS_CHAINS_ADVANCE_TICKS = 40;

/** E quantos recuam. */
export const OPEN_WORLD_BOSS_CHAINS_RETREAT_TICKS = 20;

/** A que distância a Sentença orbita, em raios de casco além do corpo. */
export const OPEN_WORLD_BOSS_ORBIT_HULL_RADII = 12;

/** De quantos em quantos tiques a órbita inverte o sentido. Contado, nunca sorteado. */
export const OPEN_WORLD_BOSS_ORBIT_FLIP_TICKS = 80;

/** Onde o anel da Sentença acaba de fechar, em raios de casco além do corpo. */
export const OPEN_WORLD_BOSS_RING_INNER_HULL_RADII = 4;

/** Quantas brasas um Rugido deixa, ao longo do eixo do cone. */
export const OPEN_WORLD_BOSS_EMBER_COUNT = 5;

/** O raio de cada brasa, em raios de casco. */
export const OPEN_WORLD_BOSS_EMBER_HULL_RADII = 6;

/** Quanto tempo uma brasa arde, em tiques. Quatro segundos. */
export const OPEN_WORLD_BOSS_EMBER_LIFE_TICKS = Math.round(4_000 / OPEN_WORLD_TICK_MS);

/**
 * **De quantos em quantos tiques uma brasa cobra.**
 *
 * A cadência canónica de dano persistente (`PVP_POISON_TICK_INTERVAL_TICKS`), e não uma
 * escolhida aqui. O relógio é o do NASCIMENTO das brasas daquele boss, e não um
 * acumulador por casco: é isso que faz áreas sobrepostas do mesmo boss não
 * acumularem sem ser preciso guardar um contador por par.
 */
export const OPEN_WORLD_BOSS_EMBER_INTERVAL_TICKS = Math.max(1, PVP_POISON_TICK_INTERVAL_TICKS);

/** Quanto uma aplicação de brasa cobra, em basis points do ataque base. */
export const OPEN_WORLD_BOSS_EMBER_DAMAGE_BPS = 1_500;

/**
 * **Os seis perfis, e são a única fonte dos números de cada golpe.**
 *
 * Uma relação total sobre as seis habilidades: acrescentar uma sétima ao
 * vocabulário sem lhe dar perfil deixa de compilar.
 */
export const OPEN_WORLD_BOSS_STRIKE_PROFILES: {
  readonly [K in OpenWorldBossMeleeBlow]: OpenWorldBossStrikeProfile;
} = Object.freeze({
  /* Boss9 — recua, aponta um corredor estreito, e atravessa-o. */
  stinger_lunge: {
    triggerHullRadii: 18,
    reachHullRadii: 0,
    halfWidthHullRadii: 2,
    windupTicks: 8,
    telegraphTicks: 12,
    stages: [{ ticks: 10, damageBps: 18_000, sectorTanBps: 0 }],
    recoveryTicks: 12,
    cooldownTicks: 60,
    knockbackPermille: 1_000,
  },
  /* Boss11 — orbita, abre um anel largo, e fecha-o para dentro. */
  circular_judgment: {
    triggerHullRadii: 24,
    reachHullRadii: 24,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 18,
    stages: [{ ticks: 10, damageBps: 16_000, sectorTanBps: 0 }],
    recoveryTicks: 10,
    cooldownTicks: 70,
    knockbackPermille: 500,
  },
  /* Boss12 — investe, ultrapassa, trava, e ruge um cone que deixa brasas. */
  ember_roar: {
    triggerHullRadii: 20,
    reachHullRadii: 20,
    halfWidthHullRadii: 0,
    windupTicks: OPEN_WORLD_BOSS_CHARGE_TICKS + OPEN_WORLD_BOSS_BRAKE_TICKS,
    telegraphTicks: 14,
    stages: [{ ticks: 4, damageBps: 14_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_45_TAN_BPS }],
    recoveryTicks: 10,
    cooldownTicks: 80,
    knockbackPermille: 1_000,
  },
  /* Boss13 — três voltas completas, uma por etapa, e um acerto por volta. */
  chain_whirl: {
    triggerHullRadii: 16,
    reachHullRadii: 16,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 16,
    stages: [
      { ticks: 12, damageBps: 7_000, sectorTanBps: 0 },
      { ticks: 12, damageBps: 7_000, sectorTanBps: 0 },
      { ticks: 12, damageBps: 7_000, sectorTanBps: 0 },
    ],
    recoveryTicks: 10,
    cooldownTicks: 80,
    knockbackPermille: 333,
  },
  /* Boss15 — maça, intervalo legível, maça. A segunda parte de onde a primeira acabou. */
  twin_chains: {
    triggerHullRadii: 20,
    reachHullRadii: 20,
    halfWidthHullRadii: 3,
    windupTicks: 0,
    telegraphTicks: 12,
    stages: [
      { ticks: 8, damageBps: 9_000, sectorTanBps: 0 },
      { ticks: 10, damageBps: 0, sectorTanBps: 0 },
      { ticks: 8, damageBps: 9_000, sectorTanBps: 0 },
    ],
    recoveryTicks: 10,
    cooldownTicks: 70,
    knockbackPermille: 667,
  },
  /* Boss16 — três varridas a abrir, com uma janela de fuga entre a segunda e a terceira. */
  reaping_fan: {
    triggerHullRadii: 14,
    reachHullRadii: 14,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 10,
    stages: [
      { ticks: 4, damageBps: 5_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_30_TAN_BPS },
      { ticks: 6, damageBps: 0, sectorTanBps: 0 },
      { ticks: 4, damageBps: 6_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_45_TAN_BPS },
      { ticks: 12, damageBps: 0, sectorTanBps: 0 },
      { ticks: 4, damageBps: 8_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_60_TAN_BPS },
    ],
    recoveryTicks: 12,
    cooldownTicks: 60,
    knockbackPermille: 333,
  },
});

/* -------------------------------------------------------------------------- */
/*  As seis magias exclusivas dos conjuradores 1 a 6 (gate V28I.BC)           */
/* -------------------------------------------------------------------------- */

/**
 * **De onde saem estes números, e porque medem o casco e não o boss.**
 *
 * Os bosses 1 a 6 vivem nos perigos 1 e 2, e são **pequenos**: 41 a 108 px de
 * raio no tier 1, contra 191 a 724 px dos corpo a corpo. Uma área medida em
 * múltiplos de `boss.radiusMu` seria mais pequena do que uma nave. A âncora é o
 * **raio de um casco** (`OPEN_WORLD_SHIP_RADIUS_MU`), que a disciplina deste
 * ficheiro já autoriza ao lado do raio do boss.
 *
 * O **gatilho** soma sempre o corpo (`R + n·r`): sem isso o corpo do boss comeria
 * parte do alcance, e um boss grande atacaria de mais perto do que um pequeno.
 *
 * O resto é relação com fontes que já existiam: telegraph ancorado nos **30
 * tiques** que eram a carga canónica de uma magia de boss, arrefecimento em
 * **60**, metade do intervalo que havia entre duas magias, empurrão em
 * `OPEN_WORLD_BOMB_KNOCKBACK_MU`, cadência persistente em
 * `PVP_POISON_TICK_INTERVAL_TICKS`, e dano em basis points do ataque base.
 *
 * **O ritmo escolhido**: 0,32 a 0,39 `A` por segundo, a mesma faixa do especial
 * de um corpo a corpo. Um corpo a corpo soma o contacto durante o arrefecimento
 * e chega a 0,55; um conjurador não encosta e não tem contacto nenhum. Ele fica
 * com cerca de dois terços do débito, e é isso que faz manter distância valer.
 *
 * Todos provisórios, e registados em `docs/OPEN_QUESTIONS.md`.
 */

/** As rotações inteiras do leque do Feixe Ocular. Literais, e nunca `Math.cos`. */
export const OPEN_WORLD_BOSS_FAN_COS_30_BPS = 8_660;
export const OPEN_WORLD_BOSS_FAN_SIN_30_BPS = 5_000;
export const OPEN_WORLD_BOSS_FAN_COS_15_BPS = 9_659;
export const OPEN_WORLD_BOSS_FAN_SIN_15_BPS = 2_588;

/** Onde a Coroa de Cinzas acaba de fechar, em raios de casco. */
export const OPEN_WORLD_BOSS_CROWN_INNER_HULL_RADII = 4;

/* --- O Enxame: os únicos corpos ofensivos que uma magia destas cria --- */

/** Quantas esferas um enxame tem. Oito, visíveis e contáveis. */
export const OPEN_WORLD_BOSS_SWARM_COUNT = 8;

/** A que distância elas orbitam antes de partir, em raios de casco. */
export const OPEN_WORLD_BOSS_SWARM_ORBIT_HULL_RADII = 6;

/** De quantos em quantos tiques uma se destaca. Uma de cada vez, sempre. */
export const OPEN_WORLD_BOSS_SWARM_RELEASE_TICKS = 5;

/** A velocidade de uma esfera, em múltiplos da velocidade do boss. */
export const OPEN_WORLD_BOSS_SWARM_SPEED_FACTOR = 10;

/** Quantos tiques uma esfera vive antes de se apagar sozinha. */
export const OPEN_WORLD_BOSS_SWARM_LIFE_TICKS = 50;

/** O corpo de colisão de uma esfera. Duas vezes o de um projétil canónico. */
export const OPEN_WORLD_BOSS_SWARM_BODY_MU = OPEN_WORLD_PROJECTILE_RADIUS_MU * 2;

/**
 * **A curvatura da espiral**, como uma rotação inteira por tique.
 *
 * Três graus, em basis points de cosseno e seno, escritos como literais. A
 * direcção é rodada e depois **renormalizada à velocidade original com `isqrt`**,
 * portanto a espiral não perde nem ganha módulo ao longo dos cinquenta tiques —
 * e não passa por `Math.cos`, que é definido pela implementação e podia
 * dessincronizar a re-simulação do servidor entre versões de Node.
 */
export const OPEN_WORLD_BOSS_SWARM_TURN_COS_BPS = 9_986;
export const OPEN_WORLD_BOSS_SWARM_TURN_SIN_BPS = 523;

/** Quanto uma esfera cobra, em basis points do ataque base. */
export const OPEN_WORLD_BOSS_SWARM_DAMAGE_BPS = 3_500;

/* --- A névoa do Sopro Pútrido --- */

/** Quanto tempo a névoa fica no chão, em tiques. Quatro segundos. */
export const OPEN_WORLD_BOSS_MIASMA_LIFE_TICKS = Math.round(4_000 / OPEN_WORLD_TICK_MS);

/**
 * **Até quando ela cobra.**
 *
 * Três segundos, e não quatro: as cobranças caem a um, dois e três segundos, e
 * ao quarto ela desaparece **sem uma quarta cobrança**. As brasas do Rugido
 * continuam a cobrar até ao fim do prazo delas, que é o que o gate V28I.BA
 * aprovou — é por isso que este limite é um campo da zona e não uma regra
 * global.
 */
export const OPEN_WORLD_BOSS_MIASMA_LAST_BURN_TICKS = Math.round(3_000 / OPEN_WORLD_TICK_MS);

/** Quanto a névoa cobra de cada vez, em basis points do ataque base. */
export const OPEN_WORLD_BOSS_MIASMA_DAMAGE_BPS = 5_000;

/**
 * **Os seis perfis das magias**, ao lado dos seis dos golpes físicos.
 *
 * A mesma forma, porque é a mesma máquina. Nenhuma delas tem `windup`: um
 * conjurador não recua nem investe antes de anunciar — ele pára e desenha.
 */
export const OPEN_WORLD_BOSS_SPELL_PROFILES: {
  readonly [K in OpenWorldBossEarlySpell]: OpenWorldBossStrikeProfile;
} = Object.freeze({
  /* Boss1 — um anel de fogo nasce à volta do alvo e fecha-se. */
  crown_of_ashes: {
    triggerHullRadii: 20,
    reachHullRadii: 14,
    halfWidthHullRadii: OPEN_WORLD_BOSS_CROWN_INNER_HULL_RADII,
    windupTicks: 0,
    telegraphTicks: 30,
    stages: [{ ticks: 20, damageBps: 22_000, sectorTanBps: 0 }],
    recoveryTicks: 10,
    cooldownTicks: 70,
    knockbackPermille: 0,
    originIsTarget: true,
    reachIncludesBody: false,
  },
  /* Boss2 — um sigilo desenha-se sob o alvo e rebenta. */
  blood_seal: {
    triggerHullRadii: 20,
    reachHullRadii: 10,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 34,
    stages: [{ ticks: 2, damageBps: 20_000, sectorTanBps: 0 }],
    recoveryTicks: 10,
    cooldownTicks: 70,
    knockbackPermille: 1_000,
    originIsTarget: true,
    reachIncludesBody: false,
  },
  /* Boss3 — um cone de esporos que fica no chão. */
  putrid_breath: {
    triggerHullRadii: 20,
    reachHullRadii: 20,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 24,
    stages: [{ ticks: 4, damageBps: 5_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_45_TAN_BPS }],
    recoveryTicks: 10,
    cooldownTicks: 66,
    knockbackPermille: 0,
    originIsTarget: false,
    reachIncludesBody: true,
  },
  /* Boss4 — cinco feixes curtos varrem um leque, um de cada vez. */
  ocular_beam: {
    triggerHullRadii: 24,
    reachHullRadii: 24,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 10,
    stages: [
      { ticks: 8, damageBps: 0, sectorTanBps: 0 },
      { ticks: 2, damageBps: 5_000, sectorTanBps: 0 },
      { ticks: 8, damageBps: 0, sectorTanBps: 0 },
      { ticks: 2, damageBps: 5_000, sectorTanBps: 0 },
      { ticks: 8, damageBps: 0, sectorTanBps: 0 },
      { ticks: 2, damageBps: 5_000, sectorTanBps: 0 },
      { ticks: 8, damageBps: 0, sectorTanBps: 0 },
      { ticks: 2, damageBps: 5_000, sectorTanBps: 0 },
      { ticks: 8, damageBps: 0, sectorTanBps: 0 },
      { ticks: 2, damageBps: 5_000, sectorTanBps: 0 },
    ],
    recoveryTicks: 12,
    cooldownTicks: 60,
    knockbackPermille: 0,
    originIsTarget: false,
    reachIncludesBody: true,
  },
  /* Boss5 — oito esferas orbitam e partem em espiral, uma a uma. */
  swarm_stance: {
    triggerHullRadii: 24,
    reachHullRadii: OPEN_WORLD_BOSS_SWARM_ORBIT_HULL_RADII,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 30,
    stages: [
      { ticks: OPEN_WORLD_BOSS_SWARM_RELEASE_TICKS, damageBps: 0, sectorTanBps: 0, acts: true },
      { ticks: OPEN_WORLD_BOSS_SWARM_RELEASE_TICKS, damageBps: 0, sectorTanBps: 0, acts: true },
      { ticks: OPEN_WORLD_BOSS_SWARM_RELEASE_TICKS, damageBps: 0, sectorTanBps: 0, acts: true },
      { ticks: OPEN_WORLD_BOSS_SWARM_RELEASE_TICKS, damageBps: 0, sectorTanBps: 0, acts: true },
      { ticks: OPEN_WORLD_BOSS_SWARM_RELEASE_TICKS, damageBps: 0, sectorTanBps: 0, acts: true },
      { ticks: OPEN_WORLD_BOSS_SWARM_RELEASE_TICKS, damageBps: 0, sectorTanBps: 0, acts: true },
      { ticks: OPEN_WORLD_BOSS_SWARM_RELEASE_TICKS, damageBps: 0, sectorTanBps: 0, acts: true },
      { ticks: OPEN_WORLD_BOSS_SWARM_RELEASE_TICKS, damageBps: 0, sectorTanBps: 0, acts: true },
    ],
    recoveryTicks: 10,
    cooldownTicks: 70,
    knockbackPermille: 0,
    originIsTarget: false,
    reachIncludesBody: true,
    /* As oito de uma vez, ou nenhuma. Reservadas, e não só verificadas. */
    reserveBodies: OPEN_WORLD_BOSS_SWARM_COUNT,
  },
  /* Boss6 — quatro faixas cardeais acendem e disparam juntas. */
  cross_of_lament: {
    triggerHullRadii: 28,
    reachHullRadii: 32,
    halfWidthHullRadii: 3,
    windupTicks: 0,
    telegraphTicks: 28,
    stages: [{ ticks: 6, damageBps: 18_000, sectorTanBps: 0 }],
    recoveryTicks: 12,
    cooldownTicks: 65,
    knockbackPermille: 0,
    originIsTarget: false,
    reachIncludesBody: false,
  },
});

/* -------------------------------------------------------------------------- */
/*  As cinco magias dos conjuradores 7, 8, 10, 14 e 17 (gate V28I.BE)         */
/* -------------------------------------------------------------------------- */

/**
 * **De onde saem estes números.**
 *
 * Estes cinco atravessam os perigos 2 a 5, e `A` vai de 27,6 a 165,4 — contra 15,6
 * a 25,1 dos seis primeiros. O boss 17 é enorme: 876 px de raio no tier 1 e
 * 2 021 px no tier 6. Por isso os alcances somam sempre o corpo (`R + n·r`), com
 * **uma excepção deliberada**: o disco do Pó Hipnótico tem raio FIXO de `16r`,
 * porque `R + 8r` daria 2 229 px no tier 6 e tornaria a fuga impossível.
 *
 * Os ciclos foram escolhidos para os cinco caírem em **0,35 a 0,37 `A` por
 * segundo**, a mesma faixa dos seis primeiros (0,32 a 0,41) e do especial de um
 * corpo a corpo (0,30 a 0,35).
 *
 * Todos provisórios, e registados em `docs/OPEN_QUESTIONS.md`.
 */

/** A força do puxão da Fome, em múltiplos da velocidade do boss. */
export const OPEN_WORLD_BOSS_PULL_SPEED_FACTOR = 3;

/**
 * **A lentidão do Pó Hipnótico**, em basis points da velocidade em vigor.
 *
 * Própria do boss, e **não a carta Lentidão**: uma criatura não equipa cartas.
 * A composição em `moveSpeedOf` é o MENOR multiplicador, nunca o produto — duas
 * lentidões a multiplicarem-se dariam uma nave parada, que ninguém aprovou.
 */
export const OPEN_WORLD_BOSS_HYPNOSIS_SPEED_BPS = 5_500;

/** Quanto tempo o pó fica no chão, em tiques. Quatro segundos. */
export const OPEN_WORLD_BOSS_DUST_LIFE_TICKS = Math.round(4_000 / OPEN_WORLD_TICK_MS);

/** O raio FIXO do pó, em raios de casco. Nunca soma o corpo do boss. */
export const OPEN_WORLD_BOSS_DUST_HULL_RADII = 16;

/** Quantos tiros uma rajada do Fogo Rotativo tem. Vinte e cinco, e são reais. */
export const OPEN_WORLD_BOSS_ROTARY_SHOTS = 25;

/** Quantos tiques uma bala vive. O alcance sai disto e da velocidade canónica. */
export const OPEN_WORLD_BOSS_ROTARY_LIFE_TICKS = 30;

/** Quanto uma bala cobra, em basis points do ataque base. Um décimo. */
export const OPEN_WORLD_BOSS_ROTARY_DAMAGE_BPS = 1_000;

/** A abertura do arco varrido, em graus, para cada lado do centro. */
export const OPEN_WORLD_BOSS_ROTARY_ARC_DEGREES = 36;

/**
 * O passo do varrimento, em basis points de cosseno e seno. Três graus.
 *
 * O MESMO par que a espiral do Enxame já usa, e pela mesma razão: literais
 * inteiros escritos à mão, porque `Math.cos` é definido pela implementação e
 * duas re-simulações do servidor podiam varrer de maneira diferente.
 */
export const OPEN_WORLD_BOSS_ROTARY_STEP_COS_BPS = OPEN_WORLD_BOSS_SWARM_TURN_COS_BPS;
export const OPEN_WORLD_BOSS_ROTARY_STEP_SIN_BPS = OPEN_WORLD_BOSS_SWARM_TURN_SIN_BPS;

/** A rotação inicial do arco: trinta e seis graus para a esquerda. */
export const OPEN_WORLD_BOSS_ROTARY_START_COS_BPS = 8_090;
export const OPEN_WORLD_BOSS_ROTARY_START_SIN_BPS = -5_878;

/** As rotações das cinco cabeças. Trinta e sessenta graus, para os dois lados. */
export const OPEN_WORLD_BOSS_MAW_COS_60_BPS = 5_000;
export const OPEN_WORLD_BOSS_MAW_SIN_60_BPS = 8_660;

/** `tan 20°`, o meio-ângulo de uma mandíbula, em basis points. */
export const OPEN_WORLD_BOSS_SECTOR_20_TAN_BPS = 3_640;

/**
 * **Os cinco perfis**, ao lado dos seis dos golpes e dos seis das magias.
 *
 * A mesma forma, porque é a mesma máquina.
 */
export const OPEN_WORLD_BOSS_LATE_SPELL_PROFILES: {
  readonly [K in OpenWorldBossLateSpell]: OpenWorldBossStrikeProfile;
} = Object.freeze({
  /* Boss7 — o vazio puxa, e depois colapsa. */
  hunger: {
    triggerHullRadii: 20,
    reachHullRadii: 24,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 30,
    stages: [
      /* O puxão. Não cobra: ele desloca, e o deslocamento é o aviso a cumprir-se. */
      { ticks: 40, damageBps: 0, sectorTanBps: 0, acts: true, announces: true },
      /* O colapso, num raio muito menor: quem fugiu está fora dele. */
      { ticks: 4, damageBps: 26_000, sectorTanBps: 0, reachHullRadii: 8 },
    ],
    recoveryTicks: 10,
    cooldownTicks: 66,
    knockbackPermille: 0,
    originIsTarget: false,
    reachIncludesBody: true,
  },
  /* Boss8 — cinco ondas, e ficar entre duas é seguro. */
  choir_of_faces: {
    triggerHullRadii: 24,
    reachHullRadii: 28,
    /* Para uma onda este campo é a ESPESSURA total: a banda é metade dela. */
    halfWidthHullRadii: 3,
    windupTicks: 0,
    telegraphTicks: 26,
    stages: [
      { ticks: 10, damageBps: 5_500, sectorTanBps: 0 },
      { ticks: 6, damageBps: 0, sectorTanBps: 0 },
      { ticks: 10, damageBps: 5_500, sectorTanBps: 0 },
      { ticks: 6, damageBps: 0, sectorTanBps: 0 },
      { ticks: 10, damageBps: 5_500, sectorTanBps: 0 },
      { ticks: 6, damageBps: 0, sectorTanBps: 0 },
      { ticks: 10, damageBps: 5_500, sectorTanBps: 0 },
      { ticks: 6, damageBps: 0, sectorTanBps: 0 },
      { ticks: 10, damageBps: 5_500, sectorTanBps: 0 },
    ],
    recoveryTicks: 10,
    cooldownTicks: 40,
    knockbackPermille: 0,
    originIsTarget: false,
    reachIncludesBody: true,
    /* Ele precisa de espaço para as ondas: a banda dele é mais afastada. */
    bandNearHullRadii: 20,
    bandFarHullRadii: 28,
  },
  /* Boss10 — vinte e cinco balas reais, varrendo setenta e dois graus. */
  rotary_fire: {
    triggerHullRadii: 24,
    reachHullRadii: 24,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 24,
    stages: Object.freeze(
      Array.from({ length: OPEN_WORLD_BOSS_ROTARY_SHOTS }, () => ({
        ticks: 2,
        damageBps: 0,
        sectorTanBps: 0,
        /* Não cobra por área: cada etapa faz nascer UMA bala. */
        acts: true,
      })),
    ),
    recoveryTicks: 12,
    cooldownTicks: 50,
    knockbackPermille: 0,
    originIsTarget: false,
    reachIncludesBody: true,
    /* A rajada inteira é reservada antes do primeiro tiro. Nunca parcial. */
    reserveBodies: OPEN_WORLD_BOSS_ROTARY_SHOTS,
  },
  /* Boss14 — cinco mandíbulas, uma de cada vez, com folgas seguras. */
  five_maws: {
    triggerHullRadii: 24,
    reachHullRadii: 22,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 12,
    stages: [
      { ticks: 8, damageBps: 0, sectorTanBps: 0, announces: true },
      { ticks: 3, damageBps: 5_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_20_TAN_BPS },
      { ticks: 5, damageBps: 0, sectorTanBps: 0 },
      { ticks: 8, damageBps: 0, sectorTanBps: 0, announces: true },
      { ticks: 3, damageBps: 5_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_20_TAN_BPS },
      { ticks: 5, damageBps: 0, sectorTanBps: 0 },
      { ticks: 8, damageBps: 0, sectorTanBps: 0, announces: true },
      { ticks: 3, damageBps: 5_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_20_TAN_BPS },
      { ticks: 5, damageBps: 0, sectorTanBps: 0 },
      { ticks: 8, damageBps: 0, sectorTanBps: 0, announces: true },
      { ticks: 3, damageBps: 5_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_20_TAN_BPS },
      { ticks: 5, damageBps: 0, sectorTanBps: 0 },
      { ticks: 8, damageBps: 0, sectorTanBps: 0, announces: true },
      { ticks: 3, damageBps: 5_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_20_TAN_BPS },
    ],
    recoveryTicks: 12,
    cooldownTicks: 45,
    knockbackPermille: 0,
    originIsTarget: false,
    reachIncludesBody: true,
  },
  /* Boss17 — o pó abranda, e depois os olhos abrem. */
  hypnotic_dust: {
    triggerHullRadii: 20,
    reachHullRadii: OPEN_WORLD_BOSS_DUST_HULL_RADII,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 26,
    stages: [
      /* O pó cai. Não fere: ele abranda, e é a preparação. */
      { ticks: 4, damageBps: 0, sectorTanBps: 0, acts: true, announces: true },
      /* Fica no chão. Cinquenta e seis tiques em que só a lentidão existe. */
      { ticks: 56, damageBps: 0, sectorTanBps: 0, announces: true },
      /* Os olhos das asas abrem: o aviso da ignição, e o pó aquece. */
      { ticks: 20, damageBps: 0, sectorTanBps: 0, announces: true },
      /* A ignição, no primeiro tique. A zona desaparece no mesmo instante. */
      { ticks: 2, damageBps: 28_000, sectorTanBps: 0 },
    ],
    recoveryTicks: 10,
    cooldownTicks: 40,
    knockbackPermille: 0,
    originIsTarget: true,
    /* O raio é FIXO. Somar o corpo tornaria a fuga impossível nos tiers grandes. */
    reachIncludesBody: false,
  },
});

/* -------------------------------------------------------------------------- */
/*  Os três kits dos finais 18, 19 e 20 (gate V28I.BG)                        */
/* -------------------------------------------------------------------------- */

/**
 * **De onde saem estes números.**
 *
 * Os três finais são enormes: o boss 20 no tier 6 tem 3 580 px de raio e 9 421 px
 * de largura, mais do que os 3 840 px da área visível. **Nenhuma geometria destas
 * habilidades pode ser medida a partir do centro deles sem verificar a fuga**, e é
 * por isso que o anel do Decreto tem uma largura fixa e os núcleos da Sentença
 * pousam à volta do ALVO e não do boss.
 *
 * O resto é relação com o que já existia: a velocidade de um teleguiado é a
 * canónica de um míssil, o intervalo da pressão sai de um gerador do livro de
 * RNG da sala, e os limiares são frações da vida máxima do próprio boss — nunca
 * do número de participantes.
 *
 * Todos provisórios, e registados em `docs/OPEN_QUESTIONS.md`.
 */

/* --- Os teleguiados inevitáveis --- */

/** Um teleguiado por alvo por boss. O limite é estrutural, e não uma contagem. */
export const OPEN_WORLD_BOSS_HOMING_PER_TARGET = 1;

/**
 * **O tempo mínimo de voo de um teleguiado**, em milissegundos (gate V28I.BW.5F).
 *
 * Uma habilidade inevitável tem de ser LEGÍVEL: quem a leva precisa de a ter
 * visto a sair, a atravessar e a chegar. Antes disto o corpo nascia na
 * superfície do boss já dentro do alcance do casco e resolvia-se no mesmo tique
 * — `cast` e `impact` juntos, e o pico de corpos em voo no fim de um tique era
 * ZERO. Nenhuma fotografia o apanhava, e portanto nenhum cliente o podia
 * desenhar sem fingir uma viagem que já tinha acontecido.
 *
 * Estes 400 ms são o mínimo autoritativo, e não um atraso de apresentação: o
 * corpo existe, persegue e é publicado durante a janela inteira, e o dano só
 * pode resolver-se no fim dela.
 */
export const OPEN_WORLD_BOSS_HOMING_MIN_FLIGHT_MS = 400;

/**
 * **O mesmo, em tiques.** `400 / 50 = 8`, exato e sem resto.
 *
 * A divisão está aqui em vez de um `8` escrito à mão pela mesma razão que a do
 * míssil: se algum dia a duração ou o tique deixarem de dividir, isto passa a
 * ser fracionário e o erro aparece numa prova em vez de aparecer como um
 * teleguiado que vive 7,84 tiques.
 */
export const OPEN_WORLD_BOSS_HOMING_MIN_FLIGHT_TICKS =
  OPEN_WORLD_BOSS_HOMING_MIN_FLIGHT_MS / OPEN_WORLD_TICK_MS;

/** O que cada teleguiado cobra, em basis points do ataque base. */
export const OPEN_WORLD_BOSS_HARPOON_DAMAGE_BPS = 9_000;
export const OPEN_WORLD_BOSS_SPEAR_DAMAGE_BPS = 8_000;
export const OPEN_WORLD_BOSS_GAZE_DAMAGE_BPS = 10_000;

/** O corpo de cada um, em raios de casco. */
export const OPEN_WORLD_BOSS_HARPOON_HULL_RADII = 2;
export const OPEN_WORLD_BOSS_SPEAR_HULL_RADII = 1.5;
export const OPEN_WORLD_BOSS_GAZE_HULL_RADII = 2.5;

/** O aviso de cada um, em tiques. */
export const OPEN_WORLD_BOSS_HARPOON_TELEGRAPH_TICKS = 20;
export const OPEN_WORLD_BOSS_SPEAR_TELEGRAPH_TICKS = 16;
export const OPEN_WORLD_BOSS_GAZE_TELEGRAPH_TICKS = 24;

/** O arrefecimento de cada um, em tiques. */
export const OPEN_WORLD_BOSS_HARPOON_COOLDOWN_TICKS = 120;
export const OPEN_WORLD_BOSS_SPEAR_COOLDOWN_TICKS = 100;
export const OPEN_WORLD_BOSS_GAZE_COOLDOWN_TICKS = 140;

/* --- O golpe de pressão, e o relógio dele --- */

/**
 * **O intervalo entre dois golpes de pressão**, em tiques.
 *
 * Dois a seis segundos, inclusive nos dois extremos. Sorteado pelo servidor de
 * uma finalidade PRÓPRIA do livro de RNG — separada das outras porque a sequência
 * de um gerador é observada pelo saque seguinte, e partilhá-lo faria a cadência
 * de um boss depender de quantas joias alguém traz.
 */
export const OPEN_WORLD_BOSS_PRESSURE_MIN_TICKS = 40;
export const OPEN_WORLD_BOSS_PRESSURE_MAX_TICKS = 120;

/* --- O Bombardeio de Profundidade --- */

/** Quantos obuses os quatro canhões disparam. Um por canhão. */
export const OPEN_WORLD_BOSS_SHELL_COUNT = 4;

/** O raio de cada círculo no chão, em raios de casco. */
export const OPEN_WORLD_BOSS_SHELL_HULL_RADII = 10;

/** Quanto tempo passa entre a marca e o impacto. */
export const OPEN_WORLD_BOSS_SHELL_FLIGHT_TICKS = 24;

/** Quanto cada impacto cobra, em basis points do ataque base. */
export const OPEN_WORLD_BOSS_SHELL_DAMAGE_BPS = 7_000;

/** A que distância do alvo os quatro círculos se espalham, em raios de casco. */
export const OPEN_WORLD_BOSS_SHELL_SPREAD_HULL_RADII = 14;

/* --- A Salva Coral --- */

/** Quantas lâminas-pena a salva crava no campo. */
export const OPEN_WORLD_BOSS_FEATHER_COUNT = 7;

/** O comprimento e a meia-largura de cada linha, em raios de casco. */
export const OPEN_WORLD_BOSS_FEATHER_LENGTH_HULL_RADII = 28;
export const OPEN_WORLD_BOSS_FEATHER_HALF_WIDTH_HULL_RADII = 2;

/** Quanto tempo as sete ficam cravadas antes de detonarem juntas. */
export const OPEN_WORLD_BOSS_FEATHER_ARM_TICKS = 30;

/** Quanto cada uma cobra, em basis points do ataque base. */
export const OPEN_WORLD_BOSS_FEATHER_DAMAGE_BPS = 4_500;

/** O passo angular entre duas lâminas: dezoito graus, e as folgas são reais. */
export const OPEN_WORLD_BOSS_FEATHER_STEP_COS_BPS = 9_511;
export const OPEN_WORLD_BOSS_FEATHER_STEP_SIN_BPS = 3_090;
/** A rotação inicial: cinquenta e quatro graus para a esquerda. */
export const OPEN_WORLD_BOSS_FEATHER_START_COS_BPS = 5_878;
export const OPEN_WORLD_BOSS_FEATHER_START_SIN_BPS = -8_090;

/* --- A Mordida da Matilha --- */

/** Quantas cabeças a montaria tem. Cinco, e a arte mostra-as todas diferentes. */
export const OPEN_WORLD_BOSS_PACK_HEADS = 5;

/** `tan 35°`, o meio-ângulo de uma mordida, em basis points. */
export const OPEN_WORLD_BOSS_SECTOR_35_TAN_BPS = 7_002;

/* --- As ultimates: quando, e quanto --- */

/**
 * **Os três marcos de vida**, em basis points da vida máxima.
 *
 * Setenta e cinco, cinquenta e vinte e cinco por cento. Cada um é consumido uma
 * vez por geração, e um golpe que atravesse dois de uma vez consome **o mais
 * alto ainda por consumir** e deixa o outro pendente.
 */
export const OPEN_WORLD_BOSS_ULTIMATE_MILESTONES_BPS = [7_500, 5_000, 2_500] as const;

/**
 * **O tecto de tempo**, em tiques, para uma luta parada escalar na mesma.
 *
 * Sessenta segundos desde a resolução da ultimate anterior — ou desde a aquisição
 * do primeiro alvo. O relógio **só corre com alvo**: sem ele pausa, e não
 * acumula, portanto sair do alcance e voltar não adianta nem atrasa nada.
 */
export const OPEN_WORLD_BOSS_ULTIMATE_TIMEOUT_TICKS = 1_200;

/** Quanto tempo um final fica atordoado depois de uma ultimate interrompida. */
export const OPEN_WORLD_BOSS_ULTIMATE_STUN_TICKS = 60;

/** Quantos tiques sem nenhum envolvido cancelam uma canalização. */
export const OPEN_WORLD_BOSS_ULTIMATE_EMPTY_TICKS = 20;

/** O aviso e a canalização do Colapso do Abismo. */
export const OPEN_WORLD_BOSS_COLLAPSE_TELEGRAPH_TICKS = 40;
export const OPEN_WORLD_BOSS_COLLAPSE_CHANNEL_TICKS = 160;

/**
 * **O limiar de interrupção do Colapso**, em basis points da vida MÁXIMA do boss.
 *
 * Três por cento, e **nunca escalado pelos participantes**. Um limiar
 * proporcional ao número de pilotos puniria o grupo por ser grupo: quatro teriam
 * de fazer quatro vezes mais, e a mecânica ficaria idêntica a solo com mais
 * risco. Fixo na vida do boss, um grupo interrompe mais depressa — e é essa a
 * recompensa de ser grupo.
 */
export const OPEN_WORLD_BOSS_COLLAPSE_THRESHOLD_BPS = 300;

/** O aviso e a duração da Sentença. */
export const OPEN_WORLD_BOSS_SENTENCE_TELEGRAPH_TICKS = 40;
export const OPEN_WORLD_BOSS_SENTENCE_CHANNEL_TICKS = 200;

/** A vida de cada núcleo, em basis points da vida máxima do boss. */
export const OPEN_WORLD_BOSS_CORE_HEALTH_BPS = 150;

/** O raio de um núcleo, em raios de casco. */
export const OPEN_WORLD_BOSS_CORE_HULL_RADII = 6;

/** A que distância do alvo congelado os dois núcleos pousam, em raios de casco. */
export const OPEN_WORLD_BOSS_CORE_OFFSET_HULL_RADII = 12;

/** O aviso e o fecho do Decreto. */
export const OPEN_WORLD_BOSS_DECREE_TELEGRAPH_TICKS = 100;
export const OPEN_WORLD_BOSS_DECREE_CLOSE_TICKS = 4;

/**
 * **A largura do anel do Decreto**, em raios de casco.
 *
 * Quarenta e oito, e é uma LARGURA e não um raio. A borda interior é o corpo do
 * boss e a exterior é `R + 48r`.
 *
 * **As duas distâncias, e a que importa é a pior** (corrigido no gate V28I.BI):
 *
 * - quem está a meio da banda tem as duas saídas, e a mais próxima fica a **24r
 *   = 624 px** — 1,84 s a 340 px/s;
 * - quem só pode sair **para fora** — porque está encostado à borda interior, ou
 *   porque o corpo do boss lhe fecha o caminho de dentro — percorre a banda
 *   inteira: **48r = 1 248 px**, ou cerca de **3,67 s**.
 *
 * As duas cabem no aviso de 5 s, e é a segunda que o dimensiona. O número é
 * constante em qualquer tier porque a banda é medida em raios de CASCO.
 *
 * Um disco cheio centrado no boss seria matematicamente impossível: no tier 6 a
 * fuga do centro seriam 4 204 px, ou 12,4 segundos.
 */
export const OPEN_WORLD_BOSS_DECREE_BAND_HULL_RADII = 48;

/**
 * **Os três golpes de pressão dos finais.** A mesma forma, a mesma máquina.
 */
export const OPEN_WORLD_BOSS_FINAL_PROFILES: {
  readonly [K in OpenWorldBossFinalPressure]: OpenWorldBossStrikeProfile;
} = Object.freeze({
  /* Boss18 — quatro obuses em arco, quatro círculos, impactos sequenciais. */
  depth_bombardment: {
    triggerHullRadii: 28,
    reachHullRadii: OPEN_WORLD_BOSS_SHELL_HULL_RADII,
    halfWidthHullRadii: 0,
    windupTicks: 0,
    telegraphTicks: 30,
    stages: [
      /* O voo: os quatro nascem no primeiro tique e os círculos ficam marcados. */
      {
        ticks: OPEN_WORLD_BOSS_SHELL_FLIGHT_TICKS,
        damageBps: 0,
        sectorTanBps: 0,
        acts: true,
        announces: true,
      },
      { ticks: 6, damageBps: OPEN_WORLD_BOSS_SHELL_DAMAGE_BPS, sectorTanBps: 0 },
      { ticks: 6, damageBps: OPEN_WORLD_BOSS_SHELL_DAMAGE_BPS, sectorTanBps: 0 },
      { ticks: 6, damageBps: OPEN_WORLD_BOSS_SHELL_DAMAGE_BPS, sectorTanBps: 0 },
      { ticks: 6, damageBps: OPEN_WORLD_BOSS_SHELL_DAMAGE_BPS, sectorTanBps: 0 },
    ],
    recoveryTicks: 14,
    cooldownTicks: 0,
    knockbackPermille: 1_000,
    originIsTarget: true,
    reachIncludesBody: false,
    reserveBodies: OPEN_WORLD_BOSS_SHELL_COUNT,
  },
  /* Boss19 — sete lâminas cravadas, e todas detonam na mesma batida. */
  choral_volley: {
    triggerHullRadii: 28,
    reachHullRadii: OPEN_WORLD_BOSS_FEATHER_LENGTH_HULL_RADII,
    halfWidthHullRadii: OPEN_WORLD_BOSS_FEATHER_HALF_WIDTH_HULL_RADII,
    windupTicks: 0,
    telegraphTicks: 26,
    stages: [
      /* As sete cravam-se e ficam visíveis; nada fere ainda. */
      { ticks: OPEN_WORLD_BOSS_FEATHER_ARM_TICKS, damageBps: 0, sectorTanBps: 0, announces: true },
      /* A batida: as sete juntas, e um casco só apanha as que o contêm. */
      { ticks: 4, damageBps: OPEN_WORLD_BOSS_FEATHER_DAMAGE_BPS, sectorTanBps: 0 },
    ],
    recoveryTicks: 12,
    cooldownTicks: 0,
    knockbackPermille: 0,
    originIsTarget: false,
    reachIncludesBody: false,
  },
  /* Boss20 — uma cabeça da matilha investe, e é sempre a seguinte do ciclo. */
  pack_bite: {
    triggerHullRadii: 24,
    reachHullRadii: 20,
    halfWidthHullRadii: 0,
    windupTicks: 8,
    telegraphTicks: 22,
    stages: [{ ticks: 4, damageBps: 24_000, sectorTanBps: OPEN_WORLD_BOSS_SECTOR_35_TAN_BPS }],
    recoveryTicks: 14,
    cooldownTicks: 0,
    knockbackPermille: 1_000,
    originIsTarget: false,
    reachIncludesBody: true,
  },
});

/**
 * **O perfil de qualquer habilidade**, física ou mágica. A única leitura.
 *
 * Quatro tabelas e um acesso: as vinte habilidades partilham a mesma forma de
 * perfil, e quem as consome não precisa de saber de que família uma delas é.
 */
export const OPEN_WORLD_BOSS_ABILITY_PROFILES: {
  readonly [K in OpenWorldBossStrikeAbility]: OpenWorldBossStrikeProfile;
} = Object.freeze({
  ...OPEN_WORLD_BOSS_STRIKE_PROFILES,
  ...OPEN_WORLD_BOSS_SPELL_PROFILES,
  ...OPEN_WORLD_BOSS_LATE_SPELL_PROFILES,
  ...OPEN_WORLD_BOSS_FINAL_PROFILES,
});

/* -------------------------------------------------------------------------- */
/*  Pesca — o que é FIXO, e o que é PROVISÓRIO (gate V28I.M)                   */
/* -------------------------------------------------------------------------- */

/**
 * **O que NÃO está aqui.**
 *
 * As espécies, as vidas-base e as faixas de peso vivem no catálogo
 * (`shared/src/open-world/fish.ts`), porque são propriedades das criaturas. A
 * capacidade de doze e os três minutos de reposição vivem no contrato
 * (`shared/src/open-world/fishing.ts`), porque são decisões do dono e não
 * equilíbrio.
 *
 * O que está aqui é o resto: o tamanho de uma água, quanto se espera pela
 * mordida, quanto cada raridade multiplica a vida, e com que frequência cada
 * raridade aparece. **Todos provisórios**, e nenhum deles calibrado por
 * ninguém.
 */

/**
 * O raio de uma área aquática, em milli-unidades.
 *
 * Grande: quarenta raios de casco, o que dá um disco de oitenta raios de
 * diâmetro — um pequeno planeta aquático em que se entra e se fica, e não uma
 * poça que se atravessa sem dar por ela. Provisório.
 */
export const OPEN_WORLD_WATER_RADIUS_MU = OPEN_WORLD_SHIP_RADIUS_MU * 40;

/**
 * **Quanto tempo até morder**, em tiques, entre estes dois limites.
 *
 * O instante exacto é selado pelo servidor a partir do gerador da sala e do
 * identificador da tentativa: determinístico para quem re-simula, e
 * imprevisível para quem não tem a semente. Provisório, e escolhido para a
 * espera ser real sem ser aborrecida.
 */
export const OPEN_WORLD_FISH_BITE_MIN_TICKS = Math.round(4_000 / OPEN_WORLD_TICK_MS);
export const OPEN_WORLD_FISH_BITE_MAX_TICKS = Math.round(12_000 / OPEN_WORLD_TICK_MS);

/**
 * **Quanto cada raridade multiplica a vida de um peixe**, em basis points.
 *
 * Dez mil é um. Cresce monotonicamente com o nível da raridade — é isso que faz
 * "raridade maior possui multiplicador de vida maior" ser verdade por
 * construção, e não por três números terem calhado bem.
 *
 * Uma tabela TOTAL sobre `Rarity`: uma raridade nova é um erro de compilação
 * aqui, e não uma raridade cujo peixe tem multiplicador zero.
 *
 * Provisórios. O que está decidido é a forma, e não a curva.
 */
export const OPEN_WORLD_FISH_RARITY_HEALTH_BPS: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 10_000,
  uncommon: 12_000,
  rare: 15_000,
  epic: 19_000,
  mythic: 24_000,
  legendary: 30_000,
  excellent: 38_000,
  perfect: 48_000,
  anomaly: 62_000,
  prismatic: 80_000,
});

/**
 * **Com que frequência cada raridade sai da água**, em partes de um total.
 *
 * Não há distribuição canónica para reaproveitar: a da caça é de bosses
 * (`appearanceParts`) e a do cofre é de equipamento — nenhuma delas é sobre
 * peixes. Estes pesos são **provisórios** e criados para este gate.
 *
 * As três propriedades que o contrato exige:
 *
 * - as comuns aparecem mais, e cada degrau acima aparece menos;
 * - **todas** têm parte positiva, portanto nenhuma é inalcançável;
 * - o total é a soma, e o sorteio é do servidor.
 *
 * A economia final **não está equilibrada** e isto está registado para
 * calibração.
 */
/**
 * **A vida de referência cresce estritamente com a posição** (gate V28I.BK).
 *
 * A metade da monotonia que o catálogo não pode provar sozinho: a força-base
 * vive em `shared` e o multiplicador de raridade vive aqui, e a propriedade é
 * dos dois juntos. No peso de referência o fator `peso / pesoReferência` vale
 * um, portanto a vida de referência é `forçaBase x multiplicador` e mais nada.
 *
 * Corre ao carregar, e não numa suíte: uma tabela mal editada tem de falhar no
 * arranque, e não na primeira pescaria de um jogador.
 *
 * As espécies retiradas ficam de fora — elas não têm posição, e não estão na
 * progressão.
 */
function assertFishReferenceHealthGrowsByPosition(): void {
  const byPosition = [...FISH_SPECIES_LIST].sort((a, b) => a.position - b.position);
  let previous = 0n;
  for (const species of byPosition) {
    const reference = fishHealthMilli({
      species,
      weightMg: species.referenceWeightMg,
      rarityMultiplierBps: OPEN_WORLD_FISH_RARITY_HEALTH_BPS[species.rarity],
    });
    if (reference <= previous) {
      throw new RangeError(
        `fish reference health must grow strictly by position; ${species.id} at ${species.position} is ${reference} after ${previous}`,
      );
    }
    previous = reference;
  }
}

assertFishReferenceHealthGrowsByPosition();

export const OPEN_WORLD_FISH_RARITY_PARTS: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 3_600,
  uncommon: 2_400,
  rare: 1_700,
  epic: 1_100,
  mythic: 620,
  legendary: 340,
  excellent: 150,
  perfect: 60,
  anomaly: 24,
  prismatic: 6,
});

/** O total das partes. Somado e não escrito: as duas coisas divergiriam. */
export const OPEN_WORLD_FISH_RARITY_TOTAL_PARTS = RARITIES.reduce(
  (total, rarity) => total + OPEN_WORLD_FISH_RARITY_PARTS[rarity],
  0,
);

/**
 * **Que raridade um sorteio uniforme produz.**
 *
 * Percorre as raridades pela ordem canónica e acumula. O chamador deve um
 * sorteio uniforme em `[0, OPEN_WORLD_FISH_RARITY_TOTAL_PARTS)` — um resto de um
 * gerador mais largo não é uniforme, e a este denominador enviesaria para as
 * comuns, que é o mesmo que dizer que os peixes raros são mais raros do que a
 * tabela diz.
 */
export function openWorldFishRarityForDraw(draw: number): Rarity {
  let cursor = 0;
  for (const rarity of RARITIES) {
    cursor += OPEN_WORLD_FISH_RARITY_PARTS[rarity];
    if (draw < cursor) {
      return rarity;
    }
  }
  /*
   * Inalcançável enquanto as partes somarem o total. Recusa em vez de devolver
   * prismático em silêncio: a tabela ter sido editada sem ninguém somar outra
   * vez é exactamente o género de coisa que tem de fazer barulho.
   */
  throw new RangeError(`open world fish rarity parts do not cover ${draw}`);
}

export function assertOpenWorldRoomArithmeticIsExact(): void {
  /*
   * O raio BASE mais largo que a área de interesse usa. Os envelopes por
   * entidade somam-se-lhe e não têm tecto, mas nenhum deles pode ser maior do
   * que o quadrante — e a comparação de distâncias que os consome trabalha
   * sobre separações dentro dele.
   */
  const widest = OPEN_WORLD_AOI_OUTER_MU;
  const worstSum = 2 * widest * widest;
  if (!Number.isSafeInteger(worstSum)) {
    throw new RangeError(
      `open world interest radius (${widest} mu) is too wide to compare exactly in integers`,
    );
  }
  if (OPEN_WORLD_AOI_INNER_MU >= OPEN_WORLD_AOI_OUTER_MU) {
    throw new RangeError(
      'the open world interest area needs an outer radius wider than its inner one, or an entity on the edge appears and disappears every tick',
    );
  }
  /*
   * And the missile's reach, which is the widest separation the steering maths
   * is ever handed. `pvpMissileAimPoint` and `pvpAimIsInsideTurn` square it
   * directly, so if a future change widened the missile's life or speed past
   * what an integer can square exactly, this fails at startup instead of
   * silently bending one missile in one fight.
   */
  const reachSum = 2 * OPEN_WORLD_MISSILE_REACH_MU * OPEN_WORLD_MISSILE_REACH_MU;
  if (!Number.isSafeInteger(reachSum)) {
    throw new RangeError(
      `open world missile reach (${OPEN_WORLD_MISSILE_REACH_MU} mu) is too far to steer exactly in integers`,
    );
  }
  if (!Number.isSafeInteger(OPEN_WORLD_BOSS_HOMING_MIN_FLIGHT_TICKS)) {
    /*
     * O mínimo de legibilidade de um teleguiado é dito em milissegundos porque é
     * assim que um olho o mede; o que a simulação conta são tiques. Se um dia os
     * dois deixarem de dividir, isto falha ao arrancar em vez de dar a um
     * teleguiado uma janela fracionária que nenhuma prova consegue afirmar.
     */
    throw new RangeError(
      `open world boss homing minimum flight (${OPEN_WORLD_BOSS_HOMING_MIN_FLIGHT_MS} ms) must divide the tick (${OPEN_WORLD_TICK_MS} ms) exactly`,
    );
  }
  if (OPEN_WORLD_AOI_OUTER_MU >= OPEN_WORLD_QUADRANT_SIDE_MU) {
    /*
     * Not a correctness failure, but a design one: a view as wide as the world
     * is not an interest area, and every claim about what a frame does not
     * reveal would stop being true.
     */
    throw new RangeError(
      'the open world interest area must be smaller than a quadrant, or nothing is ever filtered',
    );
  }
}

/* -------------------------------------------------------------------------- */
/*  As dezasseis cartas (gate V28I.P)                                         */
/* -------------------------------------------------------------------------- */

/**
 * **Um quadrante corre as cartas do DUELO, e com os números do duelo.**
 *
 * ## Porque não há um único número novo aqui
 *
 * Porque não há uma única decisão nova. As dezasseis foram equilibradas para
 * uma arena que avança **50 ms por tique**, e um quadrante avança 50 ms por
 * tique: `OPEN_WORLD_TICK_MS` é literalmente `PVP_TICK_MS`. Durações,
 * arrefecimentos, alcances, cargas e probabilidades caem exatamente na mesma
 * grelha, e reescrevê-los aqui seria abrir uma segunda tabela de equilíbrio que
 * divergiria da primeira na primeira afinação.
 *
 * As distâncias já estão em basis points do raio do casco, e o raio do casco
 * deste mundo **é** o do duelo (`OPEN_WORLD_SHIP_RADIUS_MU`), portanto uma
 * Lança Solar tem aqui o mesmo comprimento que tem lá.
 *
 * O que estas linhas são: aliases, escritos como todos os outros deste ficheiro,
 * para que um leitor deste mundo veja o que ele usa sem ter de abrir o duelo.
 */

/** A carga da Esfera Ígnea: **quinze segundos**, como no duelo. */
export const OPEN_WORLD_FIREBALL_CHARGE_MS = PVP_FIREBALL_CHARGE_MS;

/** "Toda a gente", em bps do raio do casco: um alcance maior do que o mundo. */
export const OPEN_WORLD_CARD_UNLIMITED_REACH_BPS = PVP_CARD_UNLIMITED_REACH_BPS;

/** A fração do rumo que um corpo teleguiado corrige por tique. */
export const OPEN_WORLD_CARD_HOMING_BPS = PVP_CARD_HOMING_BPS;

/** A velocidade de um corpo de carta, em bps da de um tiro. */
export const OPEN_WORLD_CARD_PROJECTILE_SPEED_BPS = PVP_CARD_PROJECTILE_SPEED_BPS;

/** A Esfera sai a direito, portanto não tem curva de perseguição a abrir. */
export const OPEN_WORLD_FIREBALL_SPEED_BPS = PVP_FIREBALL_SPEED_BPS;

/** E a de um dardo de gelo, que não persegue com curva fechada. */
export const OPEN_WORLD_ICE_BOLT_SPEED_BPS = PVP_ICE_BOLT_SPEED_BPS;

/** Um corpo de carta vive mais do que um tiro: ele tem de atravessar o mundo. */
export const OPEN_WORLD_CARD_PROJECTILE_LIFETIME_TICKS = PVP_CARD_PROJECTILE_LIFETIME_TICKS;

/** **APROVADO: seis vezes o raio da Bomba Atômica.** */
export const OPEN_WORLD_CATOLE_BLAST_RADIUS_MU = PVP_CATOLE_BLAST_RADIUS_MU;

/** **APROVADO: três vezes o empurrão da Bomba Atômica.** */
export const OPEN_WORLD_CATOLE_KNOCKBACK_MU = PVP_CATOLE_KNOCKBACK_MU;

/** O empurrão de referência, para a mesma física ler a mesma escala. */
export const OPEN_WORLD_BOMB_KNOCKBACK_MU = PVP_BOMB_KNOCKBACK_MU;

/** Os degraus das duas durações sorteadas, e a hipótese da paralisia total. */
export const OPEN_WORLD_FREEZE_DURATION_STEPS = PVP_FREEZE_DURATION_STEPS;
export const OPEN_WORLD_FREEZE_ATTACK_BLOCK_BPS = PVP_FREEZE_ATTACK_BLOCK_BPS;
export const OPEN_WORLD_SLOW_DURATION_STEPS = PVP_SLOW_DURATION_STEPS;

/** A boca de uma invocação, à frente do corpo dela. */
export const OPEN_WORLD_SUMMON_MUZZLE_MU = PVP_SUMMON_MUZZLE_MU;

/* -------------------------------------------------------------------------- */
/*  Interest area                                                             */
/* -------------------------------------------------------------------------- */

/*
 * **A área de interesse deriva da CÂMARA** (gate V28I.AN.1).
 *
 * Antes derivava de segundos de voo: quarenta segundos para entrar, cinquenta
 * para sair. Era um número honesto, mas não respondia à pergunta que a área de
 * interesse existe para responder — *o que é que este jogador consegue ver?* —
 * e o gate V28I.AN parou por não conseguir fechar a derivação sem saber quanto
 * é que um ecrã mostra.
 *
 * O dono decidiu (ver `../viewport.ts`): a área visível máxima é
 * **3840 x 2160 pixeis lógicos** e o zoom mínimo é **1**. A partir daqui a
 * derivação fecha, e passa a ter esta forma:
 *
 * ```
 * raio de entrada = alcance máximo da câmara     (a diagonal INTEIRA do envelope)
 *                 + aproximação máxima x aviso   (o que se anda entre pulsos)
 *                 + envelope real da entidade    (somado por entidade, não aqui)
 * ```
 *
 * **A regra que isto não pode quebrar:** a área de interesse nunca é reduzida
 * por desempenho. Ela encolheu porque passou a ser DERIVADA, e cada parcela
 * abaixo está provada. Se um dia a câmara afastar, o envelope crescer ou um
 * corpo ficar mais rápido, estes números sobem sozinhos.
 *
 * Este bloco vive no FIM do ficheiro de propósito: agora depende do catálogo
 * inteiro do mundo — velocidades de casco, alienígena, boss, esfera e míssil —
 * e nenhuma delas está declarada antes daqui.
 */

/**
 * **Quantos tiques de aviso um corpo dá antes de poder importar: vinte.**
 *
 * O mesmo vinte de sempre, com o mesmo contrato e sem alteração — era
 * `OPEN_WORLD_AOI_PROJECTILE_LEAD_TICKS` e passa a valer para TODAS as
 * famílias, porque a margem de movimento passou a ser uma só.
 *
 * A regra que ele existe para segurar continua a ser esta: **um projétil nunca
 * pode acertar num casco de que o cliente não tinha sido avisado.** Vinte
 * tiques é um segundo à cadência autoritativa, contra uma flutuação de rede
 * medida em dezenas de milissegundos. **Provisório**, e nunca desce abaixo do
 * que uma medição de latência real justificar.
 */
export const OPEN_WORLD_AOI_LEAD_TICKS = 20;

/**
 * **O passo máximo de um casco: 23 630 mu por tique.**
 *
 * Derivado, e não escrito: `17 000 x (100 % + 39 %)`.
 *
 * `composeMoveSpeedBps` não tem tecto nenhum — só recusa bónus negativos — e
 * durante muito tempo isso pareceu querer dizer que a velocidade de um casco
 * era ilimitada. Não é, e a prova está no catálogo: **uma única** definição de
 * joia concede velocidade de movimento (`jewels.ts`), o resolvedor corta-a em
 * `JEWEL_MOVE_SPEED_MAX_BPS` antes da soma, e esse corte é absoluto por
 * construção. Um perfil adulterado teria de furar dois `min` independentes.
 *
 * Isto é o oposto do TAMANHO de um casco, que **não** tem tecto (ver
 * `openWorldHullEnvelopeMu`): a velocidade tem um máximo derivável e o tamanho
 * não tem, e por isso a velocidade entra aqui como constante e o tamanho entra
 * por entidade.
 */
export const OPEN_WORLD_MAX_HULL_TRAVEL_MU_PER_TICK = Math.trunc(
  (OPEN_WORLD_SHIP_TRAVEL_MU_PER_TICK * (10_000 + JEWEL_MOVE_SPEED_MAX_BPS)) / 10_000,
);

/**
 * **O corpo mais rápido do mundo aberto: 62 000 mu por tique, o projétil.**
 *
 * Reauditado neste gate em vez de presumido, porque desde que o vinte foi
 * escrito nasceram mísseis, Esferas Ígneas de boss, alienígenas e bosses:
 *
 * | corpo | mu/tique |
 * | --- | ---: |
 * | **projétil primário** | **62 000** |
 * | míssil | 37 572 |
 * | casco, no tecto das joias | 23 630 |
 * | alienígena | 11 900 |
 * | boss | 4 165 |
 *
 * O máximo é tirado da lista e não escolhido: acrescentar um corpo mais rápido
 * a este `Math.max` alarga a área de interesse no mesmo compromisso.
 */
export const OPEN_WORLD_FASTEST_BODY_MU_PER_TICK = Math.max(
  OPEN_WORLD_PROJECTILE_SPEED_MU_PER_TICK,
  OPEN_WORLD_MISSILE_SPEED_MU_PER_TICK,
  OPEN_WORLD_MAX_HULL_TRAVEL_MU_PER_TICK,
  OPEN_WORLD_MOB_TRAVEL_MU_PER_TICK,
  OPEN_WORLD_BOSS_TRAVEL_MU_PER_TICK,
);

/**
 * **A aproximação máxima entre um observador e o que ele pode vir a ver.**
 *
 * Os DOIS movem-se, e por isso somam-se: o observador é sempre um casco, e o
 * que ele observa pode ser qualquer corpo. `23 630 + 62 000 = 85 630` mu por
 * tique é a maior taxa a que a distância entre eles consegue encurtar.
 *
 * Usar só a velocidade do alvo deixaria de fora um caçador a voar direito a um
 * projétil parado à sua frente, que é exatamente o caso que uma perseguição
 * produz.
 */
export const OPEN_WORLD_AOI_CLOSING_MU_PER_TICK =
  OPEN_WORLD_MAX_HULL_TRAVEL_MU_PER_TICK + OPEN_WORLD_FASTEST_BODY_MU_PER_TICK;

/** O que essa aproximação percorre durante o aviso: 1 712 600 mu. */
export const OPEN_WORLD_AOI_MOVEMENT_MU =
  OPEN_WORLD_AOI_CLOSING_MU_PER_TICK * OPEN_WORLD_AOI_LEAD_TICKS;

/**
 * **O raio de entrada, sem o envelope da entidade: 8 761 903 mu.**
 *
 * `7 049 303` (a diagonal inteira da área visível máxima, 6144 x 3456 — o
 * afastamento de 0,3125× do gate V28I.BX.2.1, ADR 0125) `+ 1 712 600` (a
 * aproximação durante o aviso). Era `4 405 815 + 1 712 600 = 6 118 415` com o
 * envelope 4K. Quem chama soma-lhe o envelope REAL de cada candidato — o corpo
 * de um cometa, o raio de uma água, o casco de uma nave — porque o que decide se
 * uma coisa aparece é o BORDO dela tocar o ecrã, e não o centro.
 */
export const OPEN_WORLD_AOI_INNER_MU = OPEN_WORLD_VISIBLE_REACH_MU + OPEN_WORLD_AOI_MOVEMENT_MU;

/**
 * **A banda da histerese: 3 400 000 mu, exatamente a de hoje.**
 *
 * Era `(50 - 40) segundos x 340 000 mu/s`, e é este valor ao `mu`. Continua a
 * ser um valor absoluto e não uma fração do raio, para que a mudança de
 * derivação **não** mexa na histerese: uma entidade entra no raio interno e só
 * sai passado o externo, e sem a banda um casco parado exatamente na borda
 * entrava e saía em tiques alternados — vinte anúncios por segundo de uma nave
 * que não se mexe, e um sprite a piscar.
 *
 * O gate exige prova aritmética completa para a mexer, e este gate não a tem:
 * ela fica onde estava.
 */
export const OPEN_WORLD_AOI_HYSTERESIS_MU = 3_400_000;

/** O raio de saída, sem o envelope da entidade: a mesma banda, deslocada. */
export const OPEN_WORLD_AOI_OUTER_MU = OPEN_WORLD_AOI_INNER_MU + OPEN_WORLD_AOI_HYSTERESIS_MU;

/**
 * **O que um casco consegue desenhar A PARTIR DE SI, em bps do seu raio.**
 *
 * A Lança Solar, que é o mais comprido: noventa vezes o raio do casco. Um casco
 * a lançá-la ocupa noventa e um raios de ecrã, e se ele não for enviado a Lança
 * também não é desenhada — portanto o envelope dele tem de a conter, esteja ela
 * acesa ou não. Ser conservador aqui custa candidatos; ser exato custaria uma
 * Lança que aparece do nada a meio do ecrã.
 *
 * O alcance "ilimitado" das cartas (`OPEN_WORLD_CARD_UNLIMITED_REACH_BPS`)
 * **não** entra: é um alcance de ALVO — "toda a gente da sala" — e o que ele
 * produz é desenhado sobre o alvo, que tem a visibilidade dele próprio.
 */
export const OPEN_WORLD_HULL_DRAW_REACH_BPS = OPEN_WORLD_BOSS_LANCE_REACH_BPS;

/**
 * **O envelope visual de um casco. Sem tecto, por decisão do dono.**
 *
 * `hullRadiusFromScaleBps` cresce linearmente com a vida máxima e não tem
 * limite: mil vezes a vida de referência dá um raio de vinte e seis mil pixeis,
 * maior do que o ecrã inteiro. Não há, portanto, número nenhum que se possa
 * escrever aqui — o envelope tem de ser o da entidade concreta, lido no momento
 * em que a visibilidade é decidida.
 *
 * A fase larga do índice espacial escreve cada entidade nas células que este
 * envelope toca (gate V28I.AO), e a decisão final usa o mesmo envelope. Nenhuma
 * das duas presume um máximo, e nenhuma delas faz o tamanho de uma nave custar
 * a quem está longe dela.
 */
export function openWorldHullEnvelopeMu(hullRadiusMu: number): number {
  return hullRadiusMu + Math.trunc((hullRadiusMu * OPEN_WORLD_HULL_DRAW_REACH_BPS) / 10_000);
}

/**
 * **O envelope de uma coisa que não carrega raio próprio.**
 *
 * Três famílias precisam dele, e por razões parecidas:
 *
 * - **invocações** e **caixas de despojos**, que não têm campo de raio;
 * - os **eventos pontuais** — uma morte, uma joia largada, o rebentamento de
 *   uma carta — que são um ponto no mapa e desenham uma explosão à volta dele.
 *
 * Inventar um número para elas seria escrever uma decisão de design que
 * ninguém tomou. Em vez disso usam o MAIOR envelope estático que este ficheiro
 * declara, tirado da lista e não escolhido. É generoso por construção: nunca é
 * menor do que o que elas desenham, portanto nunca escondem nada, e o preço é
 * examinar alguns candidatos a mais.
 */
export const OPEN_WORLD_AOI_STATIC_ENVELOPE_MU = Math.max(
  OPEN_WORLD_WATER_RADIUS_MU,
  OPEN_WORLD_CATOLE_BLAST_RADIUS_MU,
  OPEN_WORLD_BOMB_BLAST_RADIUS_MU,
  OPEN_WORLD_PLANET_BODY_RADIUS_MU,
  OPEN_WORLD_COMET_BODY_RADIUS_MU,
);

/* -------------------------------------------------------------------------- */
/*  Aggro: detecção e trela                                                    */
/* -------------------------------------------------------------------------- */

/*
 * **Um inimigo só te nota depois de tu o poderes ver** (gate V28I.AN.1).
 *
 * ## Isto é uma alteração INTENCIONAL de balanceamento
 *
 * Escrito por extenso porque é: a deteção e a trela de alienígenas e de bosses
 * MUDAM de valor, e a mudança foi decidida em vez de sofrida.
 *
 * Estas quatro grandezas derivavam de `OPEN_WORLD_AOI_INNER_MU` — `/2` para a
 * deteção, `x 3/4` para a trela — e os comentários que as acompanhavam
 * declaravam a intenção: *"um mob nunca pode começar a perseguir alguém que
 * ainda não o consegue ver"*. A intenção era boa e a âncora estava errada. A
 * área de interesse nunca foi o que se vê: era uma distância de VOO, e era
 * quase o triplo do ecrã.
 *
 * O contrato 4K permite medi-lo pela primeira vez, e o resultado é este:
 *
 * | grandeza | antes | agora |
 * | --- | ---: | ---: |
 * | deteção | 6 800 px | **1 080 px** |
 * | trela | 10 200 px | **1 620 px** |
 * | disco garantido no ecrã | 1 080 px | 1 080 px |
 *
 * Antes, um alienígena começava a perseguir de **6 800 px**, quando o pixel
 * mais longe que um jogador chega a ver está a 4 406 px — e isso no melhor
 * caso, encostado a um canto do quadrante. Em qualquer outra posição via-se
 * ainda menos. Um jogador podia ser adotado como alvo, perseguido e atacado por
 * uma coisa que nunca esteve no ecrã dele. Passa a não poder.
 *
 * ## A derivação, uma só para as duas famílias
 *
 * Alienígenas e bosses partilham estas duas constantes em vez de terem cada um
 * as suas com o mesmo número dentro. Era assim que a divergência começava.
 */

/**
 * **A que distância um inimigo te nota: 1 080 000 mu.**
 *
 * O disco que está GARANTIDAMENTE no ecrã
 * ({@link OPEN_WORLD_VISIBLE_GUARANTEED_MU}), e não o alcance máximo da câmara.
 *
 * A diferença é a promessa. O alcance máximo — 4 406 px — só se atinge num canto
 * do quadrante e só na direção do canto oposto; prometer com ele que "um inimigo
 * só ataca quem já o vê" seria uma promessa quebrada em quase todas as posições
 * e em quase todas as direções. O disco garantido é o maior raio que cumpre a
 * regra **sempre**: em qualquer sítio do quadrante e em qualquer direção.
 *
 * Não leva margem de movimento nem envelope. Não é um raio de projeção — é uma
 * decisão de combate, e o que ela tem de garantir é que o jogador já teve o
 * inimigo à frente dos olhos antes de ele reagir. A área de interesse, que é
 * cinco vezes e meia maior, garante que ele já lá estava desenhado.
 */
export const OPEN_WORLD_AGGRO_DETECTION_MU = OPEN_WORLD_VISIBLE_GUARANTEED_MU;

/**
 * **A que distância um inimigo desiste: 1 620 000 mu.**
 *
 * Metade mais do que a deteção — a MESMA proporção de sempre, que era
 * `(AOI x 3/4) / (AOI / 2) = 3/2`. A âncora mudou; a relação não.
 *
 * Duas coisas ao mesmo tempo, e as duas obrigatórias:
 *
 * - **Maior do que a deteção**, ou um casco na fronteira exata era adotado e
 *   largado em tiques alternados. É a mesma histerese da área de interesse,
 *   pela mesma razão, e sem ela a perseguição pisca.
 * - **Um pouco para lá do que se vê de certeza.** Aos 1 620 px a perseguição
 *   continua para fora do disco garantido de 1 080 px — quem fugiu não perde o
 *   perseguidor no instante em que ele sai do meio do ecrã — mas fica muito
 *   dentro dos 4 406 px que a câmara chega a alcançar, portanto a perseguição
 *   nunca acontece longe da vista de quem foge.
 */
export const OPEN_WORLD_AGGRO_LEASH_MU = (OPEN_WORLD_AGGRO_DETECTION_MU * 3) / 2;

/**
 * **O alcance do Coração Vital: 13 600 000 mu.**
 *
 * Uma constante PRÓPRIA, e deliberadamente não um alias da área de interesse
 * nem da agressão.
 *
 * `procVitalHeal` filtrava os aliados curáveis por `OPEN_WORLD_AOI_INNER_MU`, o
 * que fazia de um número de APRESENTAÇÃO um número de MECÂNICA: a área de
 * interesse deixava de poder ser derivada da câmara sem alterar quem uma carta
 * cura. Foi essa dependência que fez o gate V28I.AN parar.
 *
 * Ao contrário da deteção e da trela, este alcance **não** muda: o dono decidiu
 * congelá-lo. O valor é exatamente o que era antes deste gate — quarenta
 * segundos de voo à velocidade base — para que a cura chegue aos mesmos
 * aliados a que chegava. Nenhum alvo entrou, nenhum saiu.
 *
 * Ele fica aqui, sozinho, para que a próxima pessoa que afine a câmara não
 * afine uma carta sem dar por isso.
 */
export const OPEN_WORLD_VITAL_HEAL_RANGE_MU = 13_600_000;

/**
 * **Em quantos passos a maior consulta da sala é dividida** (gate V28I.Z).
 *
 * Vive DEPOIS da área de interesse desde o gate V28I.AN.1, porque é dela que
 * deriva e ela passou a depender do catálogo inteiro do mundo. O valor
 * acompanha-a sozinho: a área encolheu 55 %, a célula encolheu 55 %, e a
 * vizinhança que uma consulta caminha continua a ser a mesma `17 x 17`.
 *
 * A grelha interna de `spatial-index.ts` precisa de um tamanho de célula, e
 * nenhuma constante de jogabilidade serve para o ser: uma célula não é um
 * alcance, não é uma distância e não é uma coisa que um jogador possa notar.
 * Este é o único número técnico que este gate introduz, e a derivação é esta.
 *
 * ## Porque OITO
 *
 * A **maior** consulta que a sala faz é a área de interesse —
 * {@link OPEN_WORLD_AOI_INNER_MU}, o alcance da câmara mais o aviso — e a
 * **menor** são
 * os poucos dezenas de milhares de mu de um corpo a tocar noutro. As duas
 * puxam em sentidos opostos: uma célula grande faz a consulta pequena examinar
 * meio quadrante, e uma célula pequena faz a consulta grande caminhar milhares
 * de células.
 *
 * Dividir o raio de interesse em oito é o ponto onde as duas ficam baratas ao
 * mesmo tempo:
 *
 * - a área de interesse caminha uma vizinhança de `17 x 17` células;
 * - uma consulta de combate caminha o mínimo possível, `3 x 3`;
 * - a grelha inteira tem `107 x 107` células possíveis, e só existem as que
 *   alguma coisa ocupa: a camada guarda-as num mapa, e uma célula vazia não
 *   custa memória nenhuma.
 *
 * ## O que ela obrigatoriamente ultrapassa
 *
 * Uma célula é maior do que **tudo o que se move num tique** — um casco anda
 * {@link OPEN_WORLD_SHIP_TRAVEL_MU_PER_TICK} e um projétil
 * {@link OPEN_WORLD_PROJECTILE_SPEED_MU_PER_TICK}, ambos duas ordens de
 * grandeza abaixo — portanto nenhuma trajetória de um tique atravessa mais do
 * que duas células por eixo.
 *
 * Ela **não** é maior do que todos os corpos: um casco cresce com a vida máxima
 * e não tem tecto, e a Bomba e o Catolé abrem áreas grandes. É por isso que a
 * camada alarga cada consulta pelo maior raio que indexou, em vez de confiar
 * que o corpo cabe na célula.
 *
 * ## O que este número NÃO é
 *
 * Não é um alcance, não muda deteção, não muda velocidade, não aparece no
 * protocolo nem na instantânea, não é uma unidade do mapa e não é visível. Os
 * quadrantes continuam a ser `A/1` a `J/10`. Mudá-lo só pode mudar quantas
 * comparações foram precisas — nunca o resultado de nenhuma.
 */
export const OPEN_WORLD_SPATIAL_CELL_STEPS = 8;

/** O lado de uma célula interna, em milli-unidades. Derivado, nunca escrito. */
export const OPEN_WORLD_SPATIAL_CELL_MU = Math.max(
  1,
  Math.floor(OPEN_WORLD_AOI_INNER_MU / OPEN_WORLD_SPATIAL_CELL_STEPS),
);

/**
 * **Quantas células, por eixo, uma entidade pode ocupar** (gate V28I.AO): oito.
 *
 * Uma entidade é escrita em todas as células que o corpo dela toca, para que o
 * custo de ser grande exista só onde ela está. Isso funciona enquanto o número
 * de células for pequeno; acima deste limite ela sai da grelha e passa para a
 * lista que **todas** as consultas examinam.
 *
 * ## Porque OITO, e não um número qualquer
 *
 * É o mesmo oito de {@link OPEN_WORLD_SPATIAL_CELL_STEPS}, e pela mesma razão:
 * a célula foi dimensionada para que o raio da área de interesse atravesse oito
 * delas. Uma entidade que ocupe mais células **por eixo** do que a própria área
 * de interesse atravessa é, por definição, comparável ao que qualquer pessoa
 * consegue ver — e uma coisa dessas aparece em quase todo o lado, portanto
 * examiná-la sempre é honesto e espalhá-la seria pior.
 *
 * O corte também é onde as duas contas se cruzam. Espalhar custa uma escrita por celula
 * escritas por reconstrução; estar na lista custa um candidato por consulta, e
 * uma sala faz dezenas a centenas de consultas por tique contra três
 * reconstruções. Abaixo de sessenta e quatro células espalhar é mais barato,
 * acima não é.
 *
 * ## O que ele NÃO é
 *
 * Não é um limite de tamanho. Nada é encolhido, escondido, agrupado ou omitido:
 * uma entidade acima do limite continua candidata a **todas** as consultas e
 * continua a ser testada pela geometria exata dela. O que muda é só onde ela
 * está guardada.
 */
export const OPEN_WORLD_SPATIAL_MAX_SPREAD_CELLS = OPEN_WORLD_SPATIAL_CELL_STEPS;

assertOpenWorldRoomArithmeticIsExact();
